import {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type ReactNode
} from "react";

import type {
  ApartmentTakeoffRow,
  PointVector,
  Requirement,
  ReadinessBlockerV1,
  ProjectReadinessV1,
  ScheduleEntry,
  SpecPolicyFlagSet,
  SourceRef
} from "@automatry/schemas";
import { requirementsFingerprint, SpecPolicyFlagSetSchema } from "@automatry/schemas";

import carringtonEngineFixtureUrl from "./fixtures/carrington-engine-fixture.json?url";
import carringtonProjectFixtureUrl from "./fixtures/carrington-project.json?url";
import { NotificationProvider, ToastViewport } from "./components/notifications";
import {
  TechnicalDetails,
  type TechnicalDetailsEntry
} from "./components/TechnicalDetails";
import type { CarringtonEngineFixture } from "./impactPreview";
import { type ActiveSurface, type ScreenId } from "./nav";
import { AdminRouter, ScreenRouter } from "./router";
import {
  buildProjectDisplayData,
  buildProjectWorkspaceDisplayData,
  parseProjectFixtureForDisplay,
  type ProjectFixtureWithLaneB,
  type ProjectDisplayData,
  type ProjectWorkspaceDisplayData
} from "./app/projectData";
import { ensureGenericQuotePricing } from "./app/quotePricing";
import { labelFromId } from "./app/format";
import {
  emptyDocumentRouteQuery,
  parseAppRoute,
  routeKey,
  routeToPath,
  type AppRoute,
  type CreateRouteStep,
  type DocumentRouteQuery
} from "./appRoutes";
import {
  deleteProject as deleteSavedProject,
  getProject,
  getProjectReadiness,
  listProjects,
  answerProjectReadinessEntry,
  updateCompareMatrixCell,
  waiveProjectReadinessEntry,
  type ProjectDetailResponse
} from "./app/projectsClient";
import { getLlmProviderSettings } from "./app/llmSettingsClient";
import {
  consumeLazyRouteRecovery,
  type LazyRouteRecoverySurfaceName
} from "./app/lazyRouteRecovery";
import { extractSpecifyResultFixture } from "./app/specifyJob";
import type { SpecifyProjectInput } from "./app/specifyProject";
import type {
  SpecifyRunCancelOutcome,
  SpecifyRunCompletion,
  SpecifyRunRequest
} from "./screens/SpecifyRunScreen";
import type { SpecifyStartOptions } from "./features/create/onboarding/types";
import {
  requirementsSurfaceFromEngineResults,
  type RequirementsSurfaceProps
} from "./features/requirementsEngine";
import {
  emptyDraft,
  buildSpecifyProjectInputFromDraft,
  hasMinimumApartmentTypeData,
  sourceCoverageWarningFromDocuments,
  type CreateProjectDraft,
  type SourceCoverageWarning
} from "./features/create/createDraft";
import type { ThemeName, WorkspaceMode } from "./app/types";
import { showDocumentWorkspacePane } from "./features/documents/workspaceFocus";
import {
  ApartmentColumn,
  ApartmentColumnLoading,
  AppShell,
  ProgressStepper
} from "./screens/AppChrome";
import { DetailDrawer } from "./screens/DetailDrawer";
import { DashboardLoadingState } from "./screens/DashboardLoadingState";
import { noteAttachmentOptionsFor } from "./screens/NotesRail";
import { ProjectSummaryOnlyScreen } from "./screens/ProjectSummaryOnlyScreen";
import type { SpecTab } from "./screens/SpecWorkspace";
import {
  projectsSummaryFixtureToDashboardView,
  type ProjectDashboardCard,
  type ProjectDashboardFilterKey,
  type ProjectDashboardSortKey,
  type ProjectsDashboardViewModel,
  type ProjectsSummaryFixture
} from "./viewmodel/projectsDashboardAdapter";
import type { CompareMatrixCellOverride } from "./viewmodel/spec/compareVM";
import { StepWayfinder, type OnboardingStepId, type WayfinderStep } from "./shell";
import {
  AssistantDock,
  projectNotesFromDetail,
  useProjectNotesController,
  type AssistantDockTab,
  type AssistantProposedAction,
  type ProjectNote,
  type ProjectNoteSubjectRef
} from "./features/assistant";
import {
  noteIsAppliedToRequirements,
  promotedRequirementDraft,
  promotedRequirementId as promotedRequirementIdForNote
} from "./features/create/promotedNoteRequirement";
import { rewritePolicyFlagSetProjectId } from "./features/create/policyFlagSetUtils";
import {
  useCopilotConversation,
  type AddCopilotMessageInput
} from "./features/copilot/useCopilotConversation";
import {
  fetchTenderPackStatus,
  replayTenderStage,
  type CopilotFactInput,
  type CopilotTurnRequest
} from "./liveLanes";

export {
  buildProjectDisplayData,
  parseProjectFixtureForDisplay,
  type ProjectFixtureWithLaneB,
  type ProjectDisplayData
} from "./app/projectData";
export { createLocalRailNote } from "./screens/NotesRail";
export {
  DetailDrawer,
  ScopedOverridePanel,
  scopedOverrideActionsForItem
} from "./screens/DetailDrawer";

const emptyProjectsSummaryFixture: ProjectsSummaryFixture = {
  fixture: {
    id: "projects-live",
    generatedFrom: "/api/projects",
    sourceInputs: [],
    pipelineInputMode: "server",
    generatedAt: "1970-01-01T00:00:00.000Z"
  },
  projectIds: [],
  status: "loading",
  updatedAt: "1970-01-01T00:00:00.000Z",
  projects: [],
  skippedProjects: []
};

const dashboardSourceTechnicalDetails = (idPrefix: string) => (
  <TechnicalDetails
    idPrefix={idPrefix}
    showSummaryHelper={false}
    sources={
      [
        {
          label: "Saved project source",
          detail:
            "Saved projects come from the live project API. Static dashboard fixtures are not used as a fallback.",
          tone: "info"
        }
      ] satisfies TechnicalDetailsEntry[]
    }
    data-testid={idPrefix}
  />
);

const compactAssistantQuery = "(max-width: 1180px)";

export function shouldStartAssistantOpen(activeSurface: ActiveSurface): boolean {
  if (activeSurface === "dashboard") {
    return false;
  }

  if (
    typeof globalThis.window === "undefined" ||
    typeof globalThis.window.matchMedia !== "function"
  ) {
    return true;
  }

  return !globalThis.window.matchMedia(compactAssistantQuery).matches;
}

interface OnboardingFlowAppProps extends RequirementsSurfaceProps {
  onStartSpecify: (input: SpecifyProjectInput, options?: SpecifyStartOptions) => void;
  onCancel: () => void;
  initialStep?: CreateRouteStep | undefined;
  onPersistentProjectId?: (projectId: string) => void;
  onStepChange?: (step: CreateRouteStep) => void;
  projectId?: string | undefined;
  projectDetail?: ProjectDetailResponse | undefined;
}

interface SpecifyRunScreenAppProps extends RequirementsSurfaceProps {
  request: SpecifyRunRequest;
  onComplete: (completion: SpecifyRunCompletion) => void;
  onCancel: (outcome?: SpecifyRunCancelOutcome) => void;
  onBack: () => void;
  mock?: boolean | undefined;
  projectId?: string | undefined;
  jobId?: string | undefined;
}

interface ProjectsDashboardAppProps {
  view: ProjectsDashboardViewModel;
  theme: ThemeName;
  onTheme: (theme: ThemeName) => void;
  onSort: (sort: ProjectDashboardSortKey) => void;
  onFilter: (filter: ProjectDashboardFilterKey) => void;
  onSearch: (query: string) => void;
  onOpenProject: (project: ProjectDashboardCard) => void;
  onDeleteProject: (project: ProjectDashboardCard) => Promise<void>;
  onCreateProject: () => void;
}

const OnboardingFlow = lazy(async () => {
  const module = await import("./features/create/onboarding/OnboardingFlow");

  return {
    default: module.OnboardingFlow as ComponentType<OnboardingFlowAppProps>
  };
});

interface NewTenderFlowAppProps {
  onOpenProjectScreen: (projectId: string, screen: ScreenId) => void;
  resumeProjectId?: string | undefined;
  onProjectIdChange?: ((projectId: string) => void) | undefined;
  initialProjectDetail?: ProjectDetailResponse | undefined;
}

// VIEWER-FLOW-CONTRACT-20260819.md R1: a brand new project has no
// create-wizard entry -- step 1 (Upload) IS the entry. Lazy-loaded for the
// same entry-chunk-size reason OnboardingFlow is.
const NewTenderFlow = lazy(async () => {
  const module = await import("./features/stepper/NewTenderFlow");

  return {
    default: module.NewTenderFlow as ComponentType<NewTenderFlowAppProps>
  };
});

const SpecifyRunScreen = lazy(async () => {
  const module = await import("./screens/SpecifyRunScreen");

  return {
    default: module.SpecifyRunScreen as ComponentType<SpecifyRunScreenAppProps>
  };
});

const ShowMeGuide = lazy(async () => {
  const module = await import("./features/showme/ShowMeGuide");

  return { default: module.ShowMeGuide };
});

const ProjectsDashboard = lazy(async () => {
  const module = await import("./screens/ProjectsDashboard");

  return {
    default: module.ProjectsDashboard as ComponentType<ProjectsDashboardAppProps>
  };
});

export const baseProjectsDashboardView = projectsSummaryFixtureToDashboardView(
  emptyProjectsSummaryFixture
);

export const resolveProjectChromeLabel = (input: {
  projectName?: string | null | undefined;
  activeProjectLabel?: string | null | undefined;
  summaryName?: string | null | undefined;
  workspaceLabel?: string | null | undefined;
}): string =>
  input.projectName ??
  input.activeProjectLabel ??
  input.summaryName ??
  input.workspaceLabel ??
  "Project";

const specifyWayfinderSteps: readonly WayfinderStep[] = [
  { id: "start", label: "Start", state: "done" },
  { id: "inputs", label: "Inputs", state: "done" },
  { id: "requirements", label: "Requirements", state: "done" },
  { id: "specify", label: "Specify", state: "active" }
];

export const specifyRunCancelNotice = (outcome: SpecifyRunCancelOutcome): string => {
  if (!outcome.ok) {
    return `Stopped watching the specification, but cancellation could not be confirmed: ${outcome.error} The job may still be running.`;
  }

  if (outcome.cancelled) {
    return "Specification cancelled. The server confirmed that the job was stopped.";
  }

  if (outcome.status === "running") {
    return "Cancellation was not applied. The server reports that the specification is still running; this page has stopped watching it.";
  }

  const statusLabel =
    outcome.status === "succeeded"
      ? "completed successfully"
      : outcome.status === "partial"
        ? "completed with a partial result"
        : outcome.status === "interrupted"
          ? "been interrupted"
          : "failed";

  return `Cancellation was not applied because the specification had already ${statusLabel}.`;
};

// C4-W2 fix-2: a client-side route change unmounts the previously-focused
// element, so the browser drops focus to <body> without necessarily
// resetting sequential tab navigation to the top of the document — Tab can
// land past the skip link (e.g. on the left rail) instead of on it. On every
// route change we programmatically focus this invisible, tabIndex={-1}
// sentinel that sits immediately before the skip link in the DOM; it is
// never reachable by a user's own Tab press (tabIndex={-1} is skipped by
// sequential navigation) but the very next Tab the user presses always
// lands on the skip link right after it, matching fresh-load behaviour. It
// is intentionally NOT aria-hidden — a focusable element must stay exposed
// to the accessibility tree — it is simply empty, so nothing is announced.
const ROUTE_FOCUS_RESET_ID = "tcs-route-focus-reset";

function SkipToContentLink() {
  return (
    <>
      <div id={ROUTE_FOCUS_RESET_ID} tabIndex={-1} className="sr-only" />
      <a className="skip-link" href="#main-content">
        Skip to main content
      </a>
    </>
  );
}

const routeFocusResetKeyFor = (
  route: Exclude<AppRoute, { kind: "notFound" }>
): string => {
  switch (route.kind) {
    case "dashboard":
      return "dashboard";
    case "admin":
      return `admin:${route.section}`;
    case "create":
      return "create";
    case "spec":
      return `project:${route.projectId}:spec:${route.apartmentTypeId}`;
    case "documents":
      return `project:${route.projectId}:documents`;
    case "project":
      return `project:${route.projectId}:${route.screen}`;
    case "specify":
      return `project:${route.projectId}:specify`;
    case "summary":
      return `project:${route.projectId}:summary`;
    case "tenderFlow":
      return `project:${route.projectId}:tenderFlow`;
  }
};

export const shouldLoadCarringtonProjectFixture = ({
  activeSurface,
  activeProjectId,
  hasInitialProjectFixture,
  hasProjectData
}: {
  activeSurface: ActiveSurface;
  activeProjectId: string | null;
  hasInitialProjectFixture: boolean;
  hasProjectData: boolean;
}): boolean =>
  activeSurface === "project" &&
  activeProjectId === "carrington" &&
  !hasInitialProjectFixture &&
  !hasProjectData;

export const shouldLoadCarringtonEngineFixture = ({
  activeSurface,
  activeProjectId,
  hasEngineFixture
}: {
  activeSurface: ActiveSurface;
  activeProjectId: string | null;
  hasEngineFixture: boolean;
}): boolean =>
  activeSurface === "project" && activeProjectId === "carrington" && !hasEngineFixture;

export const shouldLoadProjectEngineFixture = ({
  activeSurface,
  hasProjectData,
  hasEngineFixture
}: {
  activeSurface: ActiveSurface;
  hasProjectData: boolean;
  hasEngineFixture: boolean;
}): boolean => activeSurface === "project" && hasProjectData && !hasEngineFixture;

export const buildProjectEngineFixtureForPreview = (
  projectData: ProjectDisplayData,
  enginePackFixture: CarringtonEngineFixture
): CarringtonEngineFixture => {
  const projectId = projectData.projectFixture.project.projectId;
  const takeoffRows = projectData.apartmentSolutions.map((solution) =>
    buildProjectTakeoffRowForPreview(projectData, solution)
  );

  if (takeoffRows.length === 0) {
    throw new Error("No project apartment solutions are available for preview.");
  }

  return {
    fixture: {
      id: `ui-7-${safeId(projectId)}-engine-fixture`,
      generatedFrom: `${projectData.projectFixture.fixture.generatedFrom} + active project tender preview`,
      sourceInput: projectData.projectFixture.fixture.sourceInput,
      generatedAt: projectData.projectFixture.fixture.generatedAt
    },
    catalog: enginePackFixture.catalog,
    rules: enginePackFixture.rules,
    engineInput: {
      projectId,
      takeoffRows,
      requirements: buildProjectRequirementsForPreview(projectData)
    },
    baselines: projectData.apartmentSolutions.map((solution, index) => ({
      apartmentTypeId: solution.apartmentTypeId,
      runId: `run-${safeId(projectId)}-${safeId(solution.apartmentTypeId)}`,
      snapshotId:
        index === 0 && projectData.projectFixture.project.activeSpecSnapshotId
          ? projectData.projectFixture.project.activeSpecSnapshotId
          : `snapshot-${safeId(projectId)}-${safeId(solution.apartmentTypeId)}`,
      solutionHash: stablePreviewHash({
        projectId,
        apartmentTypeId: solution.apartmentTypeId,
        pointTotals: solution.pointLedger.required,
        riskCount: solution.risks.length
      }),
      status: solution.validation.status,
      validationStatus: solution.validation.status,
      sourceOverrideIds: [],
      productQuantities: {},
      pointTotals: solution.pointLedger.required as PointVector,
      riskCount: solution.risks.length
    }))
  };
};

export const ensureCarringtonQuotePricing = async (
  fixture: ProjectFixtureWithLaneB
): Promise<ProjectFixtureWithLaneB> => {
  if (fixture.quotePricing || fixture.project.projectId !== "carrington") {
    return fixture;
  }

  // Seed-only backfill for old persisted Carrington rows. Every normal project
  // gets quote pricing from the active catalog release via ensureProjectQuotePricing.
  if (typeof fetch !== "function") {
    const { carringtonQuotePricing } =
      await import("./fixtures/carringtonQuotePricing");

    return {
      ...fixture,
      quotePricing: carringtonQuotePricing
    };
  }

  try {
    const { carringtonQuotePricing } =
      await import("./fixtures/carringtonQuotePricing");

    return {
      ...fixture,
      quotePricing: carringtonQuotePricing
    };
  } catch (error) {
    console.warn("Could not load Carrington quote pricing module", error);
  }

  try {
    const response = await fetch(carringtonProjectFixtureUrl, {
      cache: "no-cache"
    });

    if (!response.ok) {
      return fixture;
    }

    const currentSeed = parseProjectFixtureForDisplay(await response.json());

    if (!currentSeed.quotePricing) {
      return fixture;
    }

    return {
      ...fixture,
      quotePricing: currentSeed.quotePricing
    };
  } catch (error) {
    console.warn(
      "Could not load Carrington quote pricing from the current seed",
      error
    );
    return fixture;
  }
};

const ensureProjectQuotePricing = async (
  fixture: ProjectFixtureWithLaneB
): Promise<ProjectFixtureWithLaneB> => {
  if (fixture.quotePricing) {
    return fixture;
  }

  try {
    const genericFixture = await ensureGenericQuotePricing(fixture);

    if (genericFixture.quotePricing) {
      return genericFixture;
    }
  } catch (error) {
    console.warn(
      "Could not derive quote pricing from the active catalog release",
      error
    );
  }

  return ensureCarringtonQuotePricing(fixture);
};

const withScheduleEntriesFromDetail = (
  fixture: ProjectFixtureWithLaneB,
  detail: ProjectDetailResponse | null | undefined
): ProjectFixtureWithLaneB =>
  detail
    ? {
        ...fixture,
        ...(detail.scheduleEntries
          ? { scheduleEntries: [...detail.scheduleEntries] }
          : {}),
        ...(detail.notes ? { projectNotes: [...detail.notes] } : {})
      }
    : fixture;

interface AppProps {
  initialMode?: WorkspaceMode;
  initialSelectedItemId?: string;
  initialProjectFixture?: ProjectDisplayData["projectFixture"];
  initialSelectedProjectId?: string;
  initialScreen?: ScreenId;
  initialTheme?: ThemeName;
}

type ProjectRouteIntent = Extract<
  AppRoute,
  {
    kind: "create" | "project" | "documents" | "spec" | "specify" | "summary";
  }
>;

const screenForProjectIntent = (intent?: ProjectRouteIntent | undefined): ScreenId =>
  intent?.kind === "spec"
    ? "spec"
    : intent?.kind === "documents"
      ? "documents"
      : intent?.kind === "project"
        ? intent.screen
        : "overview";

const projectRouteIntentForScreen = (
  projectId: string,
  screen: ScreenId
): ProjectRouteIntent =>
  screen === "documents"
    ? {
        kind: "documents",
        projectId,
        query: emptyDocumentRouteQuery()
      }
    : screen === "tender"
      ? { kind: "project", projectId, screen: "tender" }
      : screen === "spec"
        ? { kind: "specify", projectId }
        : { kind: "project", projectId, screen };

const draftApartmentTypesFor = (
  detail: ProjectDetailResponse
): CreateProjectDraft["apartmentTypes"] =>
  Array.isArray(detail.draft?.apartmentTypes) ? detail.draft.apartmentTypes : [];

const preSpecificationApartmentTypeId = (detail: ProjectDetailResponse): string =>
  draftApartmentTypesFor(detail)[0]?.id ?? `${detail.project.id}-pre-specification`;

const projectCanRunSpecification = (detail: ProjectDetailResponse): boolean =>
  draftApartmentTypesFor(detail).some(hasMinimumApartmentTypeData);

const preSpecificationDisabledReason = (detail: ProjectDetailResponse): string =>
  draftApartmentTypesFor(detail).length === 0
    ? "Requirements are incomplete: add at least one apartment type with homes and controls."
    : "Requirements are incomplete: complete one apartment type with a name, homes and at least one control zone.";

export const waitForWorkspaceShellPaint = (): Promise<void> =>
  new Promise((resolve) => {
    if (typeof globalThis.requestAnimationFrame === "function") {
      globalThis.requestAnimationFrame(() => {
        globalThis.setTimeout(resolve, 0);
      });
      return;
    }

    globalThis.setTimeout(resolve, 0);
  });

const yieldToMainThread = (): Promise<void> =>
  new Promise((resolve) => globalThis.setTimeout(resolve, 0));

const readCurrentRoute = (): AppRoute => {
  if (typeof globalThis.window === "undefined") {
    return { kind: "dashboard" };
  }

  if (globalThis.window.location.pathname.startsWith("/demo-viewer")) {
    globalThis.window.location.replace("/demo-viewer/index.html");
    return { kind: "dashboard" };
  }

  return parseAppRoute(
    globalThis.window.location.pathname,
    globalThis.window.location.search
  );
};

const routeRequiresProjectLoad = (route: AppRoute): boolean =>
  route.kind === "create" && route.projectId !== undefined
    ? true
    : route.kind === "project" ||
      route.kind === "documents" ||
      route.kind === "spec" ||
      route.kind === "specify" ||
      route.kind === "summary" ||
      route.kind === "tenderFlow";

const projectIdForRoute = (route: AppRoute): string | null =>
  route.kind === "create" && route.projectId
    ? route.projectId
    : route.kind === "project" ||
        route.kind === "documents" ||
        route.kind === "spec" ||
        route.kind === "specify" ||
        route.kind === "summary" ||
        route.kind === "tenderFlow"
      ? route.projectId
      : null;

export function App({
  initialMode = "simple",
  initialSelectedItemId,
  initialProjectFixture,
  initialSelectedProjectId,
  initialScreen = "overview",
  initialTheme = "light"
}: AppProps) {
  const [lazyRouteRecovery] = useState(() => consumeLazyRouteRecovery());
  const initialRoute = useMemo(() => readCurrentRoute(), []);
  const routeSyncEnabled = initialProjectFixture === undefined;
  const initialProjectId =
    initialProjectFixture?.project.projectId ??
    initialSelectedProjectId ??
    projectIdForRoute(initialRoute) ??
    null;
  const [routeRequest, setRouteRequest] = useState<AppRoute>(initialRoute);
  const [routeNotFound, setRouteNotFound] = useState<{
    attemptedPath: string;
    message: string;
    retired?: boolean;
  } | null>(() =>
    initialRoute.kind === "notFound"
      ? {
          attemptedPath: initialRoute.attemptedPath,
          message: initialRoute.message
        }
      : null
  );
  const [routingReady, setRoutingReady] = useState(
    () => !routeRequiresProjectLoad(initialRoute)
  );
  const routeApplyKeyRef = useRef<string | null>(
    routeRequiresProjectLoad(initialRoute) || initialRoute.kind === "create"
      ? null
      : routeKey(initialRoute)
  );
  const replaceNextRouteSyncRef = useRef(false);
  const projectNavigationRequestRef = useRef(0);
  // Tender can paint before its project detail resolves. Keep that early
  // request abortable so navigation can replace it instead of leaving a stale
  // loader alive alongside the destination request.
  const projectLoadAbortRef = useRef<AbortController | null>(null);
  const [theme, setTheme] = useState<ThemeName>(initialTheme);
  const [canEditLlmSettings, setCanEditLlmSettings] = useState(false);
  const [lazyRecoveryNotice] = useState<LazyRouteRecoverySurfaceName | null>(() =>
    lazyRouteRecovery && lazyRouteRecovery.source !== "session"
      ? lazyRouteRecovery.surfaceName
      : null
  );
  const [dashboardSort, setDashboardSort] = useState<ProjectDashboardSortKey>("recent");
  const [dashboardFilter, setDashboardFilter] =
    useState<ProjectDashboardFilterKey>("all");
  const [dashboardSearch, setDashboardSearch] = useState("");
  const [activeSurface, setActiveSurface] = useState<ActiveSurface>(() => {
    if (initialProjectFixture) {
      return "project";
    }

    if (initialRoute.kind === "create" && !initialRoute.projectId) {
      return "create";
    }

    if (initialRoute.kind === "admin") {
      return "admin";
    }

    return "dashboard";
  });
  const [activeProjectSummary, setActiveProjectSummary] =
    useState<ProjectDashboardCard | null>(null);
  const [activeProjectLabel, setActiveProjectLabel] = useState<string | null>(
    () =>
      initialProjectFixture?.project.name ??
      (initialProjectFixture
        ? "One Carrington / 21 Carrington Street Apartments"
        : null)
  );
  const [activeProjectId, setActiveProjectId] = useState<string | null>(
    initialProjectId
  );
  const [activeProjectDetail, setActiveProjectDetail] =
    useState<ProjectDetailResponse | null>(null);
  const [screen, setScreen] = useState<ScreenId>(() =>
    initialRoute.kind === "project"
      ? initialRoute.screen
      : initialRoute.kind === "documents"
        ? "documents"
        : initialRoute.kind === "spec"
          ? "spec"
          : initialScreen
  );
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(
    initialRoute.kind === "documents" ? (initialRoute.docId ?? null) : null
  );
  const [documentRouteQuery, setDocumentRouteQuery] = useState<DocumentRouteQuery>(
    () =>
      initialRoute.kind === "documents" ? initialRoute.query : emptyDocumentRouteQuery()
  );
  const [mode, setMode] = useState<WorkspaceMode>(initialMode);
  const [projectData, setProjectData] = useState<ProjectDisplayData | null>(() =>
    initialProjectFixture ? buildProjectDisplayData(initialProjectFixture) : null
  );
  const [loadingWorkspaceData, setLoadingWorkspaceData] =
    useState<ProjectWorkspaceDisplayData | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [engineFixture, setEngineFixture] = useState<CarringtonEngineFixture | null>(
    null
  );
  const [engineFixtureLoading, setEngineFixtureLoading] = useState(false);
  const [engineFixtureError, setEngineFixtureError] = useState<string | null>(null);
  const [selectedApartmentTypeId, setSelectedApartmentTypeId] = useState<string | null>(
    initialRoute.kind === "spec"
      ? initialRoute.apartmentTypeId
      : (initialProjectFixture?.apartmentSolutions[0]?.apartmentTypeId ?? null)
  );
  const [specTab, setSpecTab] = useState<SpecTab>(() =>
    initialRoute.kind === "spec" ? initialRoute.tab : "conn"
  );
  const [selectedItemId, setSelectedItemId] = useState<string | null>(
    initialSelectedItemId ?? null
  );
  const [onboardingStep, setOnboardingStep] = useState<CreateRouteStep>(() =>
    initialRoute.kind === "create" ? initialRoute.step : "start"
  );
  // B-RELOAD: the upload-first stepper's own project id, tracked separately
  // from the legacy wizard's activeProjectId/create-step machinery so a
  // reload (or a dashboard "Open project" click) can find its way back to
  // NewTenderFlow at the right step -- instead of stranding the user in the
  // old wizard or minting a fresh orphaned project. Non-null exactly while
  // NewTenderFlow is the surface being rendered (fresh creation in flight,
  // or resuming a known project).
  const [tenderFlowProjectId, setTenderFlowProjectId] = useState<string | null>(
    initialRoute.kind === "tenderFlow" ? initialRoute.projectId : null
  );
  const [specifyRequest, setSpecifyRequest] = useState<SpecifyRunRequest | null>(null);
  const [activeSpecifyJobId, setActiveSpecifyJobId] = useState<string | null>(null);
  const [projectsSummary, setProjectsSummary] = useState<ProjectsSummaryFixture | null>(
    null
  );
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [projectsError, setProjectsError] = useState<string | null>(null);
  const [dashboardActionError, setDashboardActionError] = useState<string | null>(null);
  const [routeNotice, setRouteNotice] = useState<string | null>(null);
  const [openingProjectId, setOpeningProjectId] = useState<string | null>(null);
  const [projectsReloadKey, setProjectsReloadKey] = useState(0);
  const dashboardView = useMemo(
    () =>
      projectsSummary
        ? projectsSummaryFixtureToDashboardView(projectsSummary, {
            sortKey: dashboardSort,
            filterKey: dashboardFilter,
            searchQuery: dashboardSearch
          })
        : null,
    [dashboardFilter, dashboardSearch, dashboardSort, projectsSummary]
  );
  const projectSummaryById = useMemo(
    () =>
      new Map(
        (dashboardView?.cards ?? []).map((project) => [project.id, project] as const)
      ),
    [dashboardView]
  );
  const deliveredProjectNames = useMemo(
    () => projectsSummary?.projects.map((project) => project.name) ?? [],
    [projectsSummary]
  );
  const requirementsSurface = useMemo(
    () => requirementsSurfaceFromEngineResults(activeProjectDetail) ?? {},
    [activeProjectDetail]
  );

  useEffect(() => {
    if (!routeSyncEnabled || typeof globalThis.window === "undefined") {
      return undefined;
    }

    const handlePopState = () => {
      const nextRoute = readCurrentRoute();

      routeApplyKeyRef.current = null;
      setRoutingReady(!routeRequiresProjectLoad(nextRoute));
      setRouteRequest(nextRoute);
    };

    globalThis.window.addEventListener("popstate", handlePopState);

    return () => {
      globalThis.window.removeEventListener("popstate", handlePopState);
    };
  }, [routeSyncEnabled]);

  useEffect(() => {
    let cancelled = false;

    void getLlmProviderSettings()
      .then((settings) => {
        if (!cancelled) {
          setCanEditLlmSettings(settings.canEdit);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCanEditLlmSettings(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(
    () => () => {
      projectLoadAbortRef.current?.abort();
      projectLoadAbortRef.current = null;
    },
    []
  );

  useEffect(() => {
    let cancelled = false;

    const loadProjects = async () => {
      try {
        setProjectsLoading(true);
        setProjectsError(null);
        const summary = await listProjects();

        if (!cancelled) {
          setProjectsSummary(summary);
        }
      } catch (error) {
        if (!cancelled) {
          setProjectsError(errorMessage(error));
        }
      } finally {
        if (!cancelled) {
          setProjectsLoading(false);
        }
      }
    };

    void loadProjects();

    return () => {
      cancelled = true;
    };
  }, [projectsReloadKey]);

  useEffect(() => {
    if (
      openingProjectId !== null ||
      !shouldLoadCarringtonProjectFixture({
        activeSurface,
        activeProjectId: activeProjectSummary?.id ?? null,
        hasInitialProjectFixture: initialProjectFixture !== undefined,
        hasProjectData: projectData !== null
      })
    ) {
      return;
    }

    let cancelled = false;

    const loadProjectFixture = async () => {
      try {
        setLoadError(null);
        const response = await fetch(carringtonProjectFixtureUrl, {
          cache: "no-store"
        });

        if (!response.ok) {
          throw new Error("fixture request failed: " + response.status);
        }

        const rawFixture: unknown = await response.json();
        const fixture = await ensureProjectQuotePricing(
          parseProjectFixtureForDisplay(rawFixture)
        );
        const nextData = buildProjectDisplayData(fixture);

        if (!cancelled) {
          setProjectData(nextData);
          setSelectedApartmentTypeId(
            (current) => current ?? nextData.workspaceView.apartmentTypeId
          );
        }
      } catch (error) {
        if (!cancelled) {
          setLoadError(error instanceof Error ? error.message : "Unknown load error");
        }
      }
    };

    void loadProjectFixture();

    return () => {
      cancelled = true;
    };
  }, [
    activeProjectSummary,
    activeSurface,
    initialProjectFixture,
    openingProjectId,
    projectData
  ]);

  useEffect(() => {
    if (
      !shouldLoadProjectEngineFixture({
        activeSurface,
        hasProjectData: projectData !== null,
        hasEngineFixture: engineFixture !== null
      })
    ) {
      return;
    }

    if (!projectData) {
      return;
    }

    let cancelled = false;

    const loadEngineFixture = async () => {
      try {
        setEngineFixtureLoading(true);
        setEngineFixtureError(null);
        const response = await fetch(carringtonEngineFixtureUrl, {
          cache: "no-store"
        });

        if (!response.ok) {
          throw new Error("engine fixture request failed: " + response.status);
        }

        const rawFixture = (await response.json()) as CarringtonEngineFixture;
        const nextFixture = buildProjectEngineFixtureForPreview(
          projectData,
          rawFixture
        );

        if (!cancelled) {
          setEngineFixture(nextFixture);
        }
      } catch (error) {
        if (!cancelled) {
          setEngineFixtureError(
            error instanceof Error ? error.message : "Unknown engine fixture error"
          );
        }
      } finally {
        if (!cancelled) {
          setEngineFixtureLoading(false);
        }
      }
    };

    void loadEngineFixture();

    return () => {
      cancelled = true;
    };
  }, [activeSurface, engineFixture, projectData]);

  const availableWorkspaceData = projectData ?? loadingWorkspaceData;
  const currentWorkspaceView = useMemo(
    () =>
      availableWorkspaceData?.workspaceViews.find(
        (view) => view.apartmentTypeId === selectedApartmentTypeId
      ) ??
      availableWorkspaceData?.workspaceView ??
      null,
    [availableWorkspaceData, selectedApartmentTypeId]
  );
  const selectedItem = useMemo(
    () =>
      currentWorkspaceView?.items.find((item) => item.id === selectedItemId) ?? null,
    [currentWorkspaceView, selectedItemId]
  );
  const noteAttachmentOptions = useMemo(
    () =>
      noteAttachmentOptionsFor({
        projectData,
        workspaceView: currentWorkspaceView
      }),
    [projectData, currentWorkspaceView]
  );
  const projectChromeLabel = resolveProjectChromeLabel({
    projectName: activeProjectDetail?.project.name,
    activeProjectLabel,
    summaryName: activeProjectSummary?.name,
    workspaceLabel: projectData?.cockpitView.projectLabel
  });
  const appAssistantContextId =
    activeProjectId ??
    (activeSurface === "dashboard"
      ? "dashboard"
      : activeSurface === "create"
        ? "create-draft"
        : activeSurface);
  const appAssistantProjectLabel =
    activeSurface === "dashboard"
      ? "Projects"
      : activeSurface === "create"
        ? (activeProjectLabel ?? "New project")
        : projectChromeLabel;
  const [appAssistantOpen, setAppAssistantOpen] = useState(() =>
    shouldStartAssistantOpen(activeSurface)
  );
  useEffect(() => {
    setAppAssistantOpen(shouldStartAssistantOpen(activeSurface));
  }, [activeSurface]);
  const [appAssistantTab, setAppAssistantTab] = useState<AssistantDockTab>("ask");
  const appAssistantActionSeq = useRef(0);
  const appAssistantMessageSinkRef = useRef<
    ((message: AddCopilotMessageInput) => string) | null
  >(null);
  const appAssistantSubjectOptions = useMemo<ProjectNoteSubjectRef[]>(
    () =>
      noteAttachmentOptions.map((option) => ({
        id: option.id,
        label: option.label,
        kind: option.kind
      })),
    [noteAttachmentOptions]
  );
  const pushAppAssistantMessage = useCallback((message: AddCopilotMessageInput) => {
    appAssistantMessageSinkRef.current?.(message);
  }, []);
  const appAssistantPersistedProjectId =
    activeSurface === "create" ||
    activeSurface === "dashboard" ||
    activeSurface === "admin"
      ? null
      : activeProjectId;
  const appAssistantInitialNotes = useMemo(
    () =>
      activeProjectDetail &&
      appAssistantPersistedProjectId &&
      activeProjectDetail.project.id === appAssistantPersistedProjectId
        ? projectNotesFromDetail(activeProjectDetail)
        : [],
    [activeProjectDetail, appAssistantPersistedProjectId]
  );
  const appAssistantNotes = useProjectNotesController({
    projectId: appAssistantContextId,
    persistentProjectId: appAssistantPersistedProjectId,
    initialNotes: appAssistantInitialNotes,
    subjectOptions: appAssistantSubjectOptions,
    onSaveError: (context, error) => {
      const action =
        context === "edit"
          ? "Project note update failed."
          : context === "delete"
            ? "Project note delete failed."
            : context === "resolve"
              ? "Project note conflict resolution failed."
              : "Project note save failed.";

      pushAppAssistantMessage({
        role: "error",
        text: `${action} ${errorMessage(error)}`
      });
    }
  });
  const appAssistantNotesHydrationKeyRef = useRef<string | null>(null);

  useEffect(() => {
    const hydrationKey =
      activeProjectDetail &&
      appAssistantPersistedProjectId &&
      activeProjectDetail.project.id === appAssistantPersistedProjectId
        ? [
            activeProjectDetail.project.id,
            activeProjectDetail.project.revision,
            activeProjectDetail.project.updatedAt,
            activeProjectDetail.notes?.length ?? 0
          ].join(":")
        : null;

    if (!hydrationKey || !activeProjectDetail) {
      appAssistantNotesHydrationKeyRef.current = null;
      return;
    }

    if (appAssistantNotesHydrationKeyRef.current === hydrationKey) {
      return;
    }

    appAssistantNotes.resetNotes(projectNotesFromDetail(activeProjectDetail));
    appAssistantNotesHydrationKeyRef.current = hydrationKey;
  }, [
    activeProjectDetail,
    appAssistantPersistedProjectId,
    appAssistantNotes.resetNotes
  ]);
  useEffect(() => {
    if (!activeProjectId || appAssistantPersistedProjectId !== activeProjectId) {
      return;
    }

    setProjectData((current) => {
      if (!current || current.projectFixture.project.projectId !== activeProjectId) {
        return current;
      }

      return buildProjectDisplayData({
        ...current.projectFixture,
        projectNotes: appAssistantNotes.notes.map((note) => ({
          ...note,
          projectId: activeProjectId
        }))
      });
    });
  }, [activeProjectId, appAssistantPersistedProjectId, appAssistantNotes.notes]);
  const applyAppAssistantNoteToRequirements = useCallback(
    async (note: ProjectNote) => {
      const promotedRequirementId = promotedRequirementIdForNote(note);

      await appAssistantNotes.editSavedNote(note.id, {
        status: "promoted_to_requirement",
        promotedRequirementId
      });
      pushAppAssistantMessage({
        role: "applied",
        text:
          note.kind === "requirement-amendment"
            ? "Requirement amendment applied. Re-run the specification so the engine works from the updated requirements."
            : "Note applied to requirements. Re-run the specification so the engine works from the updated requirements."
      });
    },
    [appAssistantNotes, pushAppAssistantMessage]
  );
  const currentSpecifyInput = useMemo(() => {
    if (!activeProjectDetail) {
      return null;
    }

    try {
      return buildCurrentSpecifyInputFromDetail(
        activeProjectDetail,
        appAssistantNotes.notes
      );
    } catch {
      return null;
    }
  }, [activeProjectDetail, appAssistantNotes.notes]);
  const requirementFingerprintState = useMemo(() => {
    if (!projectData || !currentSpecifyInput) {
      return null;
    }

    const snapshotFingerprint = snapshotRequirementsFingerprint(
      projectData.projectFixture
    );
    const currentFingerprint =
      snapshotFingerprint.kind === "full"
        ? requirementsFingerprint(requirementsFromSpecifyInput(currentSpecifyInput))
        : fixtureRequirementRowsFingerprint(
            fixtureRequirementRowsFromRequirements(
              requirementsFromSpecifyInput(currentSpecifyInput)
            )
          );

    return {
      current: currentFingerprint,
      snapshot: snapshotFingerprint.value,
      stale: currentFingerprint !== snapshotFingerprint.value
    };
  }, [currentSpecifyInput, projectData]);
  const createAppProposedActionId = useCallback((): string => {
    appAssistantActionSeq.current += 1;
    return `app-action-${appAssistantActionSeq.current}`;
  }, []);
  // B-DOCK: activeSurface stays "create" for both the legacy onboarding
  // wizard AND the upload-first tender-flow stepper (NewTenderFlow) -- see
  // isTenderFlowResume below, which uses the exact same tenderFlowProjectId
  // predicate to pick which one renders. The dock must mount on the
  // tender-flow stepper (Rich: the assistant pane must work in the
  // workspace) but stay hidden on the legacy project-less wizard, so gate on
  // tenderFlowProjectId rather than activeSurface alone.
  const appAssistantVisible =
    (activeSurface !== "create" || tenderFlowProjectId !== null) &&
    activeSurface !== "admin" &&
    !(activeSurface === "project" && screen === "viewer");
  const appAssistantFacts = useMemo<CopilotFactInput[]>(() => {
    const facts: CopilotFactInput[] = [];

    if (dashboardView) {
      facts.push({
        id: "app-dashboard-summary",
        kind: "project",
        plainText: `${dashboardView.cards.length} saved projects are visible. ${dashboardView.totals.needsUserDecisionCount} decisions and ${dashboardView.totals.blockingRiskCount} blocking risks need attention.`,
        confidence: "high"
      });
    }

    if (projectData) {
      const project = projectData.projectFixture.project;
      facts.push({
        id: "app-active-project",
        kind: "project",
        plainText: `${project.name} has ${project.approvedRequirementIds.length} approved requirement references and ${projectData.projectFixture.takeoffRows.length} take-off rows.`,
        confidence: "high"
      });

      if (currentWorkspaceView) {
        facts.push({
          id: `app-active-apartment-${currentWorkspaceView.apartmentTypeId}`,
          kind: "apartment_solution",
          plainText: `${currentWorkspaceView.title} has ${currentWorkspaceView.controllerIds.length} controllers, ${currentWorkspaceView.deviceIds.length} devices, ${currentWorkspaceView.items.length} workspace rows, and ${currentWorkspaceView.riskIds.length} risks.`,
          confidence: "high"
        });
      }
    }

    for (const fact of appAssistantNotes.noteFacts) {
      facts.push({
        id: fact.id,
        kind: "note",
        plainText: fact.plainText,
        ...(fact.refs
          ? {
              refs: fact.refs.map((ref) => ({ ...ref }))
            }
          : {}),
        ...(fact.confidence ? { confidence: fact.confidence } : {})
      });
    }

    return facts;
  }, [appAssistantNotes.noteFacts, currentWorkspaceView, dashboardView, projectData]);
  const appAssistantRequestBuilder = useCallback(
    (message: string): CopilotTurnRequest => {
      const projectId =
        activeProjectId ?? projectData?.projectFixture.project.projectId;
      const selectedObject = currentWorkspaceView
        ? {
            kind: "apartment_type" as const,
            id: currentWorkspaceView.apartmentTypeId,
            label: currentWorkspaceView.title
          }
        : undefined;
      const sourceRefs = appAssistantFacts.flatMap((fact) => fact.sourceRefs ?? []);

      return {
        sessionId: `web-assistant-${appAssistantContextId}`,
        message: message.trim(),
        routeContext: {
          routeId: `assistant/${activeSurface}`,
          view: assistantCopilotViewFor(activeSurface, screen),
          label: "Project assistant"
        },
        ...(selectedObject ? { selectedObject } : {}),
        context: {
          orgId: "automatry",
          ...(projectId ? { projectId } : {}),
          ...(selectedObject ? { selectedRefs: [selectedObject] } : {}),
          facts: appAssistantFacts,
          sourceRefs,
          ruleRefs: [],
          traceRefs: [],
          unresolvedOpenQuestions:
            projectData && projectData.cockpitView.clientClarificationCount > 0
              ? [
                  `${projectData.cockpitView.clientClarificationCount} client clarifications remain unresolved.`
                ]
              : [],
          staleStateWarnings: requirementFingerprintState?.stale
            ? ["The current requirements differ from the last specification snapshot."]
            : []
        }
      };
    },
    [
      activeProjectId,
      activeSurface,
      appAssistantContextId,
      appAssistantFacts,
      currentWorkspaceView,
      projectData,
      requirementFingerprintState?.stale,
      screen
    ]
  );
  const appAssistantConversation = useCopilotConversation({
    contextId: appAssistantContextId,
    projectId: activeProjectId ?? undefined,
    buildRequest: appAssistantRequestBuilder,
    hasContext: appAssistantFacts.length > 0,
    enabled: appAssistantVisible,
    engineFixture,
    engineFixtureLoading,
    engineFixtureError,
    apartmentTypeId: currentWorkspaceView?.apartmentTypeId
  });
  appAssistantMessageSinkRef.current = appAssistantConversation.addMessage;
  const appAssistantMessages = appAssistantConversation.messages;
  const appAssistantGroundingState = appAssistantConversation.groundingState;
  const handleAppAssistantAsk = appAssistantConversation.send;
  const handleAppAssistantProposedAction = useCallback(
    (action: AssistantProposedAction) => {
      pushAppAssistantMessage({
        role: "status",
        text: `I have held "${action.title}" as a reviewed draft. Open the relevant project screen before applying it.`
      });
    },
    [pushAppAssistantMessage]
  );
  const appAssistant =
    (activeSurface === "create" && tenderFlowProjectId === null) ||
    activeSurface === "admin" ||
    (activeSurface === "project" && screen === "viewer") ? null : (
      <AssistantDock
        open={appAssistantOpen}
        onToggle={setAppAssistantOpen}
        tab={appAssistantTab}
        onTab={setAppAssistantTab}
        projectContextId={appAssistantContextId}
        projectLabel={appAssistantProjectLabel}
        copilot={{
          messages: appAssistantMessages,
          turn: appAssistantConversation.turn,
          onAsk: handleAppAssistantAsk,
          isBusy: appAssistantConversation.isBusy,
          canAsk: appAssistantConversation.canAsk,
          unavailableMessage: appAssistantConversation.unavailableMessage,
          groundingState: appAssistantGroundingState,
          suggestedPrompts: appAssistantSuggestions(activeSurface),
          onProposedAction: handleAppAssistantProposedAction,
          createProposedActionId: createAppProposedActionId,
          inputPlaceholder: `Ask about ${appAssistantProjectLabel}...`
        }}
        notes={{
          ...appAssistantNotes.notesContract,
          subjectOptions: appAssistantSubjectOptions,
          onPromoteNote: applyAppAssistantNoteToRequirements
        }}
      />
    );
  const routeForCurrentState = useMemo<Exclude<
    AppRoute,
    { kind: "notFound" }
  > | null>(() => {
    if (activeSurface === "dashboard") {
      return { kind: "dashboard" };
    }

    if (activeSurface === "admin") {
      return { kind: "admin", section: "llm" };
    }

    if (activeSurface === "create" && tenderFlowProjectId) {
      return { kind: "tenderFlow", projectId: tenderFlowProjectId };
    }

    if (activeSurface === "create") {
      return activeProjectId
        ? { kind: "create", projectId: activeProjectId, step: onboardingStep }
        : { kind: "create", step: onboardingStep };
    }

    if (activeSurface === "specify" && activeProjectId) {
      return { kind: "specify", projectId: activeProjectId };
    }

    if (activeSurface === "summary" && activeProjectId) {
      return { kind: "summary", projectId: activeProjectId };
    }

    if (activeSurface !== "project" || !activeProjectId) {
      return null;
    }

    if (screen === "spec") {
      const apartmentTypeId =
        currentWorkspaceView?.apartmentTypeId ??
        (activeProjectDetail && isOnboardingStatus(activeProjectDetail.project.status)
          ? selectedApartmentTypeId
          : null);

      return apartmentTypeId
        ? {
            kind: "spec",
            projectId: activeProjectId,
            apartmentTypeId,
            tab: specTab
          }
        : null;
    }

    if (screen === "documents") {
      return {
        kind: "documents",
        projectId: activeProjectId,
        ...(selectedDocumentId ? { docId: selectedDocumentId } : {}),
        query: documentRouteQuery
      };
    }

    return { kind: "project", projectId: activeProjectId, screen };
  }, [
    activeProjectId,
    activeProjectDetail,
    activeSurface,
    currentWorkspaceView,
    documentRouteQuery,
    onboardingStep,
    screen,
    selectedDocumentId,
    selectedApartmentTypeId,
    specTab,
    tenderFlowProjectId
  ]);

  useEffect(() => {
    if (
      typeof globalThis.window === "undefined" ||
      !routeSyncEnabled ||
      routeNotFound ||
      !routingReady ||
      !routeForCurrentState
    ) {
      return;
    }

    if (routeApplyKeyRef.current === null) {
      return;
    }

    const nextPath = routeToPath(routeForCurrentState);
    const nextKey = routeKey(routeForCurrentState);
    const currentPath = `${globalThis.window.location.pathname || "/"}${
      globalThis.window.location.search
    }`;

    if (routeKey(routeRequest) !== nextKey) {
      setRouteRequest(routeForCurrentState);
    }

    if (currentPath === nextPath) {
      routeApplyKeyRef.current = nextKey;
      replaceNextRouteSyncRef.current = false;
      return;
    }

    const canonicalDocumentQueryReplacement =
      routeForCurrentState.kind === "documents" &&
      routeKey(routeRequest) === nextKey &&
      currentPath.split("?")[0] === nextPath.split("?")[0];
    const method =
      replaceNextRouteSyncRef.current ||
      canonicalDocumentQueryReplacement ||
      (currentPath === "/" && routeForCurrentState.kind === "dashboard")
        ? "replaceState"
        : "pushState";

    globalThis.window.history[method](
      method === "replaceState" ? globalThis.window.history.state : null,
      "",
      nextPath
    );
    routeApplyKeyRef.current = nextKey;
    replaceNextRouteSyncRef.current = false;
  }, [
    routeForCurrentState,
    routeNotFound,
    routeRequest,
    routeSyncEnabled,
    routingReady
  ]);

  // Restore "skip link is the first Tab stop" after a screen-level
  // client-side navigation (see the ROUTE_FOCUS_RESET_ID sentinel above).
  // URL durability changes inside a screen are intentionally ignored: draft
  // creation adds a project id to create routes, onboarding step URLs are
  // part of the same wizard, and spec workspace tabs are an in-workflow
  // tablist rather than a new screen.
  const routeFocusResetKey = routeForCurrentState
    ? routeFocusResetKeyFor(routeForCurrentState)
    : null;
  const lastRouteFocusResetKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (typeof document === "undefined" || routeFocusResetKey === null) {
      return;
    }

    if (lastRouteFocusResetKeyRef.current === routeFocusResetKey) {
      return;
    }

    const isFirstRun = lastRouteFocusResetKeyRef.current === null;
    lastRouteFocusResetKeyRef.current = routeFocusResetKey;

    if (isFirstRun) {
      // Fresh load already puts the skip link first in tab order; nothing
      // to restore.
      return;
    }

    const sentinel = globalThis.document.getElementById(ROUTE_FOCUS_RESET_ID);
    sentinel?.focus({ preventScroll: true });
  }, [routeFocusResetKey]);

  const showProjectShell = useCallback(
    (project: ProjectDashboardCard, intent?: ProjectRouteIntent | undefined) => {
      projectNavigationRequestRef.current += 1;

      if (typeof globalThis.performance?.mark === "function") {
        globalThis.performance.clearMarks("tcs:workspace-navigation-start");
        globalThis.performance.clearMarks("tcs:workspace-shell-painted");
        globalThis.performance.clearMarks("tcs:workspace-content-ready");
        globalThis.performance.clearMeasures("tcs:workspace-shell-paint");
        globalThis.performance.clearMeasures("tcs:workspace-content-load");
        globalThis.performance.mark("tcs:workspace-navigation-start");
      }

      setRouteNotFound(null);
      setRoutingReady(true);
      setRouteNotice(null);
      setActiveProjectSummary(project);
      setActiveProjectLabel(project.name);
      setActiveProjectId(project.id);
      setActiveProjectDetail(null);
      setProjectData(null);
      setLoadingWorkspaceData(null);
      setSelectedItemId(null);
      setSelectedApartmentTypeId(
        intent?.kind === "spec" ? intent.apartmentTypeId : null
      );
      setSpecTab(intent?.kind === "spec" ? intent.tab : "conn");
      setSelectedDocumentId(
        intent?.kind === "documents" ? (intent.docId ?? null) : null
      );
      setDocumentRouteQuery(
        intent?.kind === "documents" ? intent.query : emptyDocumentRouteQuery()
      );
      setScreen(screenForProjectIntent(intent));
      setLoadError(null);
      setEngineFixture(null);
      setEngineFixtureError(null);
      setSpecifyRequest(null);
      setActiveSpecifyJobId(null);
      setOpeningProjectId(project.id);
      setActiveSurface("project");
    },
    []
  );

  useEffect(() => {
    if (
      activeSurface !== "project" ||
      projectData ||
      !activeProjectId ||
      typeof globalThis.requestAnimationFrame !== "function"
    ) {
      return undefined;
    }

    const frame = globalThis.requestAnimationFrame(() => {
      if (typeof globalThis.performance?.mark !== "function") {
        return;
      }

      globalThis.performance.mark("tcs:workspace-shell-painted");
      try {
        globalThis.performance.measure(
          "tcs:workspace-shell-paint",
          "tcs:workspace-navigation-start",
          "tcs:workspace-shell-painted"
        );
      } catch {
        // Direct deep links can begin before the navigation mark is available.
      }
    });

    return () => globalThis.cancelAnimationFrame(frame);
  }, [activeProjectId, activeSurface, projectData]);

  useEffect(() => {
    if (!loadingWorkspaceData || typeof globalThis.performance?.mark !== "function") {
      return;
    }

    globalThis.performance.mark("tcs:workspace-content-ready");
    try {
      globalThis.performance.measure(
        "tcs:workspace-content-load",
        "tcs:workspace-navigation-start",
        "tcs:workspace-content-ready"
      );
    } catch {
      // Initial-fixture renders do not create navigation timing marks.
    }
  }, [loadingWorkspaceData]);

  const openProject = async (project: ProjectDashboardCard) => {
    showProjectShell(project);

    try {
      setDashboardActionError(null);
      await waitForWorkspaceShellPaint();
      const detail = await getProject(project.id);

      await routeProjectByStatus(project, detail);
    } catch (error) {
      setLoadError(errorMessage(error));
    } finally {
      setOpeningProjectId(null);
    }
  };
  // VIEWER-FLOW-CONTRACT-20260819.md section 4: requirements/flags/specify/
  // export re-home to today's existing screens ("navigation re-homing only,
  // no rebuilds") rather than the tender-flow shell rendering them itself.
  // This is a deliberately lighter path than routeProjectByStatus: a
  // just-created tender-flow project carries no onboarding status to route
  // by, so this loads the project detail directly and lets ScreenRouter
  // render its normal honest "loading" state for any data not yet present.
  const openTenderFlowProjectScreen = (projectId: string, targetScreen: ScreenId) => {
    setRouteNotFound(null);
    setRoutingReady(true);
    setRouteNotice(null);
    setActiveProjectId(projectId);
    setActiveSurface("project");
    setScreen(targetScreen);
    setLoadError(null);
    // Re-homing out of NewTenderFlow onto a normal project screen -- it is
    // no longer the surface being rendered.
    setTenderFlowProjectId(null);

    void getProject(projectId)
      .then((detail) => {
        setActiveProjectDetail(detail);
        setActiveProjectLabel(detail.project.name);

        if (!detail.specResult) {
          return;
        }

        const fixture = withScheduleEntriesFromDetail(
          parseProjectFixtureForDisplay(
            extractSpecifyResultFixture(detail.specResult.result)
          ),
          detail
        );

        void ensureProjectQuotePricing(fixture).then((pricedFixture) => {
          setProjectData(buildProjectDisplayData(pricedFixture));
        });
      })
      .catch((error) => {
        setLoadError(errorMessage(error));
      });
  };
  const openTenderFlowForProject = (
    projectId: string,
    projectDetail?: ProjectDetailResponse | undefined
  ) => {
    // RETIRE-H: legacy create-flow entry points retired 2026-08-20 per Rich R4;
    // every project-scoped recovery now lands in the upload-first stepper.
    const project = projectSummaryById.get(projectId);

    setRouteNotFound(null);
    setRoutingReady(true);
    setRouteNotice(null);
    setActiveProjectSummary(project ?? null);
    setActiveProjectLabel(projectDetail?.project.name ?? project?.name ?? null);
    setActiveProjectId(projectId);
    setActiveProjectDetail(projectDetail ?? null);
    setProjectData(null);
    setLoadingWorkspaceData(null);
    setSelectedItemId(null);
    setSelectedDocumentId(null);
    setDocumentRouteQuery(emptyDocumentRouteQuery());
    setSelectedApartmentTypeId(null);
    setSpecTab("conn");
    setOnboardingStep("start");
    setSpecifyRequest(null);
    setActiveSpecifyJobId(null);
    setLoadError(null);
    setEngineFixture(null);
    setEngineFixtureError(null);
    setScreen("overview");
    setTenderFlowProjectId(projectId);
    setActiveSurface("create");
  };
  const openCreateProject = () => {
    setRouteNotFound(null);
    setRoutingReady(true);
    setRouteNotice(null);
    setActiveProjectSummary(null);
    setActiveProjectLabel(null);
    setActiveProjectId(null);
    setActiveProjectDetail(null);
    setLoadingWorkspaceData(null);
    setSelectedItemId(null);
    setSelectedApartmentTypeId(null);
    setSpecTab("conn");
    setOnboardingStep("start");
    setScreen("overview");
    setLoadError(null);
    setEngineFixture(null);
    setEngineFixtureError(null);
    // A genuinely new entry always starts a fresh NewTenderFlow instance --
    // any tender-flow project id left over from a previous session must not
    // leak in here (see tenderFlowProjectId above).
    setTenderFlowProjectId(null);
    setSpecifyRequest(null);
    setActiveSpecifyJobId(null);
    setActiveSurface("create");
  };
  const reviewActiveProjectInputs = () => {
    if (!activeProjectId) {
      openCreateProject();
      return;
    }

    // RETIRE-H: the summary's former "review inputs" action now re-enters the
    // upload-first stepper, not the legacy create wizard.
    openTenderFlowForProject(activeProjectId, activeProjectDetail ?? undefined);
  };
  const startSpecifyRun = (
    input: SpecifyProjectInput,
    options: { forceNew?: boolean } & SpecifyStartOptions = {}
  ) => {
    setRouteNotFound(null);
    setRoutingReady(true);
    setRouteNotice(null);
    setSpecifyRequest({
      input,
      projectName: input.projectName,
      startAuthorized: true,
      ...(options.forceNew ? { forceNew: true } : {}),
      ...(options.sourceCoverageWarning
        ? { sourceCoverageWarning: options.sourceCoverageWarning }
        : {}),
      ...(options.batchLabel ? { batchLabel: options.batchLabel } : {}),
      ...(options.batchNotice ? { batchNotice: options.batchNotice } : {})
    });
    setActiveProjectId(input.projectId);
    setActiveProjectLabel(input.projectName);
    setActiveSpecifyJobId(null);
    setActiveSurface("specify");
  };
  const rerunCurrentSpecification = () => {
    if (!currentSpecifyInput) {
      pushAppAssistantMessage({
        role: "error",
        text: "I could not rebuild the current requirements for this project. Open the inputs and check the apartment matrix before re-running."
      });
      setAppAssistantOpen(true);
      setAppAssistantTab("ask");
      return;
    }

    startSpecifyRun(currentSpecifyInput, { forceNew: true });
  };
  const rerunRequirementsStage = useCallback(async () => {
    const tenderScope = activeProjectDetail?.tenderScope;

    if (!activeProjectId || !tenderScope?.packId) {
      throw new Error("This project has no saved tender pack to re-run.");
    }

    const status = await fetchTenderPackStatus(tenderScope.packId);

    await replayTenderStage({
      orgId: status.pack.orgId,
      projectId: activeProjectId,
      tenderId: status.pack.tenderId,
      packId: status.pack.packId,
      stage: "requirement-crawl"
    });
  }, [activeProjectDetail?.tenderScope, activeProjectId]);
  const cancelSpecifyRun = (outcome?: SpecifyRunCancelOutcome) => {
    setRouteNotFound(null);
    setRoutingReady(true);
    setRouteNotice(outcome ? specifyRunCancelNotice(outcome) : null);
    setSpecifyRequest(null);
    setActiveSpecifyJobId(null);
    setActiveSurface("create");
  };
  const specifiedProjectLabel = (fixture: ProjectFixtureWithLaneB): string =>
    fixture.inputs[0]?.name.replace(/\s+takeoff$/i, "") ||
    fixture.takeoffRows[0]?.rawLabel ||
    "New project";
  const handleSpecifiedProject = async (completion: SpecifyRunCompletion) => {
    const { fixture } = completion;
    const nextProjectLabel = specifiedProjectLabel(fixture);

    if (typeof globalThis.performance?.mark === "function") {
      globalThis.performance.clearMarks("tcs:workspace-navigation-start");
      globalThis.performance.clearMarks("tcs:workspace-shell-painted");
      globalThis.performance.clearMarks("tcs:workspace-content-ready");
      globalThis.performance.clearMeasures("tcs:workspace-shell-paint");
      globalThis.performance.clearMeasures("tcs:workspace-content-load");
      globalThis.performance.mark("tcs:workspace-navigation-start");
    }

    setProjectData(null);
    setLoadingWorkspaceData(null);
    setActiveProjectSummary(null);
    setActiveProjectLabel(nextProjectLabel);
    setActiveProjectId(fixture.project.projectId);
    setActiveProjectDetail(null);
    setSelectedApartmentTypeId(null);
    setSelectedItemId(null);
    setScreen("spec");
    setSpecTab("conn");
    setMode("simple");
    setRouteNotice(null);
    setLoadError(null);
    setEngineFixture(null);
    setEngineFixtureError(null);
    setSpecifyRequest(null);
    setActiveSpecifyJobId(null);
    setActiveSurface("project");

    await waitForWorkspaceShellPaint();
    const nextDetail = completion.browserMock
      ? null
      : await getProject(fixture.project.projectId).catch(() => null);
    const displayFixture = withScheduleEntriesFromDetail(fixture, nextDetail);
    const initialWorkspaceData = buildProjectWorkspaceDisplayData(displayFixture);

    if (completion.browserMock) {
      persistBrowserMockSpecResult(fixture.project.projectId, completion.result);
    }

    setLoadingWorkspaceData(initialWorkspaceData);
    setActiveProjectDetail(nextDetail);
    setSelectedApartmentTypeId(initialWorkspaceData.workspaceView.apartmentTypeId);
    setProjectsReloadKey((value) => value + 1);

    await yieldToMainThread();
    const pricedFixture = await ensureProjectQuotePricing(displayFixture);
    const preparedWorkspaceData =
      pricedFixture === displayFixture
        ? initialWorkspaceData
        : buildProjectWorkspaceDisplayData(pricedFixture);

    if (preparedWorkspaceData !== initialWorkspaceData) {
      setLoadingWorkspaceData(preparedWorkspaceData);
      await yieldToMainThread();
    }

    setProjectData(buildProjectDisplayData(pricedFixture, preparedWorkspaceData));
    setLoadingWorkspaceData(null);
  };
  const backToDashboard = () => {
    setRouteNotFound(null);
    setRoutingReady(true);
    setRouteNotice(null);
    setSpecifyRequest(null);
    setActiveSpecifyJobId(null);
    setActiveSurface("dashboard");
    setSelectedItemId(null);
    setTenderFlowProjectId(null);
  };
  const openLlmSettings = () => {
    setRouteNotFound(null);
    setRoutingReady(true);
    setRouteNotice(null);
    setSpecifyRequest(null);
    setActiveSpecifyJobId(null);
    setSelectedItemId(null);
    setActiveSurface("admin");
  };
  const retryProjectsLoad = () => {
    setProjectsReloadKey((value) => value + 1);
  };
  const deleteDashboardProject = async (
    project: ProjectDashboardCard
  ): Promise<void> => {
    await deleteSavedProject(project.id);
    setProjectsSummary((current) =>
      current
        ? {
            ...current,
            projectIds: current.projectIds.filter((id) => id !== project.id),
            projects: current.projects.filter(
              (candidate) => candidate.id !== project.id
            )
          }
        : current
    );
    setProjectsReloadKey((value) => value + 1);
  };
  const handleSpecifyWayfinderClick = (step: OnboardingStepId) => {
    if (step === "specify") {
      return;
    }

    // RETIRE-H: the old specify wayfinder could reopen the legacy wizard;
    // return to the upload-first stepper instead.
    const projectId = activeProjectId ?? specifyRequest?.input.projectId;

    if (projectId) {
      openTenderFlowForProject(projectId, activeProjectDetail ?? undefined);
    } else {
      openCreateProject();
    }
  };
  const applyProjectScreenNavigation = useCallback((next: ScreenId) => {
    setRouteNotFound(null);
    setRoutingReady(true);
    setRouteNotice(null);
    setScreen(next);
    setSelectedItemId(null);

    if (next === "spec") {
      setSpecTab("conn");
    }
    if (next === "documents") {
      setSelectedDocumentId(null);
      setDocumentRouteQuery(emptyDocumentRouteQuery());
    }
  }, []);
  const handleDocumentRoute = useCallback(
    (input: { docId: string | null; query: DocumentRouteQuery; replace?: boolean }) => {
      if (input.replace) {
        replaceNextRouteSyncRef.current = true;
      }

      setSelectedDocumentId(input.docId);
      setDocumentRouteQuery(input.query);
    },
    []
  );

  const handlePersistentOnboardingProjectId = useCallback((projectId: string) => {
    setActiveProjectId(projectId);
  }, []);

  const continueActiveProjectSetup = useCallback(() => {
    if (
      !activeProjectDetail ||
      !isOnboardingStatus(activeProjectDetail.project.status)
    ) {
      return;
    }

    // RETIRE-H: the old guided setup button now returns to upload-first.
    openTenderFlowForProject(activeProjectDetail.project.id, activeProjectDetail);
  }, [activeProjectDetail]);

  const openPreSpecificationEntry = useCallback(() => {
    if (
      !activeProjectDetail ||
      !currentSpecifyInput ||
      !projectCanRunSpecification(activeProjectDetail)
    ) {
      return;
    }

    startSpecifyRun(currentSpecifyInput);
  }, [activeProjectDetail, currentSpecifyInput]);

  const preSpecificationWorkspace =
    screen === "spec" &&
    activeProjectDetail &&
    isOnboardingStatus(activeProjectDetail.project.status) &&
    !availableWorkspaceData
      ? {
          canRunSpecify: projectCanRunSpecification(activeProjectDetail),
          disabledReason: preSpecificationDisabledReason(activeProjectDetail),
          onRunSpecify: openPreSpecificationEntry
        }
      : null;

  const routeProjectByStatus = async (
    project: ProjectDashboardCard,
    detail: ProjectDetailResponse,
    intent?: ProjectRouteIntent | undefined
  ): Promise<void> => {
    if (intent?.kind === "create" && !isOnboardingStatus(detail.project.status)) {
      throw new Error(`${detail.project.name} is not in the project creator.`);
    }

    if (
      intent?.kind === "specify" &&
      !isOnboardingStatus(detail.project.status) &&
      detail.project.status !== "specifying" &&
      detail.project.status !== "specified" &&
      detail.project.status !== "partial" &&
      detail.project.status !== "failed"
    ) {
      throw new Error(`${detail.project.name} is not currently specifying.`);
    }

    const detailForRouting = isOnboardingStatus(detail.project.status)
      ? normaliseOnboardingProjectDetail(detail)
      : detail;

    setActiveProjectSummary(project);
    setActiveProjectLabel(detailForRouting.project.name);
    setActiveProjectId(detailForRouting.project.id);
    setActiveProjectDetail(detailForRouting);
    setLoadError(null);
    // Every ordinary navigation this function drives is NOT the tender-flow
    // stepper surface -- clear any id left over from a previous NewTenderFlow
    // session so the "create" surface JSX below doesn't mistakenly resume it.
    // The one branch that legitimately wants the stepper (below) re-sets it.
    setTenderFlowProjectId(null);

    // The document viewer is a self-contained project surface. It does not
    // depend on a generated specification, so drafts can reach it directly
    // without being silently redirected into the creator flow.
    if (intent?.kind === "project" && intent.screen === "viewer") {
      setProjectData(null);
      setLoadingWorkspaceData(null);
      setSelectedItemId(null);
      setSelectedApartmentTypeId(null);
      setSpecifyRequest(null);
      setActiveSpecifyJobId(null);
      setRouteNotice(null);
      setScreen("viewer");
      setActiveSurface("project");
      return;
    }

    if (
      intent?.kind === "documents" &&
      !(
        (detailForRouting.project.status === "specified" ||
          detailForRouting.project.status === "partial") &&
        detailForRouting.specResult
      )
    ) {
      setProjectData(null);
      setLoadingWorkspaceData(null);
      setSpecifyRequest(null);
      setActiveSpecifyJobId(null);
      setRouteNotice(null);
      setSelectedDocumentId(intent.docId ?? null);
      setDocumentRouteQuery(intent.query);
      setScreen("documents");
      setActiveSurface("project");
      return;
    }

    // B-RELOAD (J4 finding): a dashboard "Open project" click carries no
    // route intent at all -- distinct from an explicit /overview or /tender
    // URL visit, both handled by the tenderScope branch just below. This was
    // the one recovery path J4 found still landing an upload-first project
    // in the old wizard, blind to its own pack, even though the pack is
    // genuinely there server-side. A resolved `tenderScope: null` is also a
    // real packless tender-flow project and must land on Upload; only an
    // omitted field is treated as an older/unknown response. The already
    // fetched detail is passed into NewTenderFlow so it derives its first
    // step from this same response instead of showing a blank Upload screen
    // during a duplicate fetch.
    if (
      intent === undefined &&
      isOnboardingStatus(detailForRouting.project.status) &&
      projectHasResolvedTenderScope(detailForRouting)
    ) {
      setProjectData(null);
      setLoadingWorkspaceData(null);
      setSelectedApartmentTypeId(null);
      setSpecifyRequest(null);
      setActiveSpecifyJobId(null);
      setRouteNotice(null);
      setOnboardingStep("start");
      setActiveSurface("create");
      setTenderFlowProjectId(detailForRouting.project.id);
      return;
    }

    // Tender intake is project-scoped like Documents and does not depend on a
    // generated specification workspace. Keep the creator gate for every other
    // project/specification screen, but let a fresh project classify its pack.
    // A "Project overview" click gets the same pass when the project is known
    // to have tender-intake content -- server-truth first (ProjectDetailResponse
    // .tenderScope, populated from the same repository lookup the Tender
    // ingestion screen itself uses), with this browser's localStorage as a
    // fallback signal only. Without either check an in-progress or even fully
    // certified pack looks identical to a blank draft and the user is bounced
    // into "Resume project setup" with no way back short of a hard reload --
    // and a server-only signal is what makes that true on a browser/session
    // that never wrote the localStorage key in the first place.
    if (
      isOnboardingStatus(detailForRouting.project.status) &&
      intent?.kind === "project" &&
      (intent.screen === "tender" ||
        (intent.screen === "overview" && projectHasKnownTenderScope(detailForRouting)))
    ) {
      setProjectData(null);
      setLoadingWorkspaceData(null);
      setSelectedApartmentTypeId(null);
      setSpecifyRequest(null);
      setActiveSpecifyJobId(null);
      setRouteNotice(null);
      setScreen("tender");
      setActiveSurface("project");
      return;
    }

    // B-TOKNAV (J5 finding): same fall-through class as the tender/overview
    // carve-out above, but for the "Token usage" nav item. TokenUsageScreen
    // reads ProjectDetailResponse.costRollup directly (wired from
    // activeProjectDetail, not projectData) and does not depend on a
    // generated specification -- so a tender-flow project with known
    // tenderScope should reach it directly instead of being redirected into
    // the legacy "Resume project setup" wizard.
    if (
      isOnboardingStatus(detailForRouting.project.status) &&
      intent?.kind === "project" &&
      intent.screen === "tokenUsage" &&
      projectHasKnownTenderScope(detailForRouting)
    ) {
      setProjectData(null);
      setLoadingWorkspaceData(null);
      setSelectedApartmentTypeId(null);
      setSpecifyRequest(null);
      setActiveSpecifyJobId(null);
      setRouteNotice(null);
      setScreen("tokenUsage");
      setActiveSurface("project");
      return;
    }

    if (
      isOnboardingStatus(detailForRouting.project.status) &&
      projectHasWorkspaceContent(detailForRouting) &&
      (intent === undefined ||
        (intent.kind === "project" && intent.screen === "overview"))
    ) {
      replaceNextRouteSyncRef.current = true;
      await routeProjectByStatus(
        project,
        detail,
        workspaceIntentForProjectContent(detailForRouting)
      );
      return;
    }

    const browserMockIntent = canUseBrowserMockSpecResult(intent) ? intent : null;
    const browserMockWorkspace =
      !detailForRouting.specResult && browserMockIntent
        ? await loadBrowserMockSpecResult(detailForRouting.project.id)
        : null;

    if (browserMockIntent && browserMockWorkspace) {
      setRouteNotice(null);
      let nextScreen: ScreenId;
      let nextApartmentTypeId: string;
      let nextSpecTab: SpecTab = "conn";

      if (browserMockIntent.kind === "spec") {
        const apartmentTypeExists = browserMockWorkspace.data.workspaceViews.some(
          (view) => view.apartmentTypeId === browserMockIntent.apartmentTypeId
        );

        if (!apartmentTypeExists) {
          throw new Error(
            `${detailForRouting.project.name} does not include apartment type "${browserMockIntent.apartmentTypeId}".`
          );
        }

        nextScreen = "spec";
        nextApartmentTypeId = browserMockIntent.apartmentTypeId;
        nextSpecTab = browserMockIntent.tab;
      } else {
        nextScreen = browserMockIntent.screen;
        nextApartmentTypeId = browserMockWorkspace.data.workspaceView.apartmentTypeId;
      }

      setProjectData(browserMockWorkspace.data);
      setLoadingWorkspaceData(null);
      setSelectedApartmentTypeId(nextApartmentTypeId);
      setSpecifyRequest(null);
      setActiveSpecifyJobId(null);
      setScreen(nextScreen);
      setSpecTab(nextSpecTab);
      setActiveSurface("project");
      return;
    }

    if (
      isOnboardingStatus(detailForRouting.project.status) &&
      intent?.kind === "spec"
    ) {
      setProjectData(null);
      setLoadingWorkspaceData(null);
      setSelectedApartmentTypeId(intent.apartmentTypeId);
      setSpecifyRequest(null);
      setActiveSpecifyJobId(null);
      setRouteNotice(null);
      setScreen("spec");
      setSpecTab(intent.tab);
      setActiveSurface("project");
      return;
    }

    if (isOnboardingStatus(detailForRouting.project.status)) {
      const requestedStep =
        intent?.kind === "create"
          ? intent.step
          : createRouteStepForProject(detailForRouting);
      const allowedStep = furthestLegitimateCreateStep(detailForRouting);
      const nextStep = clampCreateRouteStep(requestedStep, allowedStep);

      if (intent?.kind === "specify") {
        replaceNextRouteSyncRef.current = true;
        setRouteNotice(
          `Specify opens after the review steps are complete. We returned you to ${createStepLabel(nextStep)} so the project stays consistent.`
        );
      } else if (intent?.kind === "create" && nextStep !== intent.step) {
        replaceNextRouteSyncRef.current = true;
        setRouteNotice(createStepGateNotice(intent.step, nextStep));
      } else {
        setRouteNotice(null);
      }

      setProjectData(null);
      setLoadingWorkspaceData(null);
      setSpecifyRequest(null);
      setActiveSpecifyJobId(null);
      setOnboardingStep(nextStep);
      setActiveSurface("create");
      return;
    }

    if (detail.project.status === "specifying" || detail.project.status === "failed") {
      setRouteNotice(null);
      const jobId = detail.activeJob?.id ?? detail.project.activeJobId;
      const request = specifyRunRequestFromProject(detail);

      if (!jobId) {
        throw new Error(
          `${detail.project.name} is ${detail.project.status}, but no active job id was returned.`
        );
      }

      if (!request) {
        throw new Error(
          `${detail.project.name} is ${detail.project.status}, but no persisted specification request was returned.`
        );
      }

      setProjectData(null);
      setLoadingWorkspaceData(null);
      setSpecifyRequest(request);
      setActiveSpecifyJobId(jobId);
      setActiveSurface("specify");
      return;
    }

    if (detail.project.status === "specified" || detail.project.status === "partial") {
      if (!detail.specResult) {
        if (
          intent &&
          !(
            intent.kind === "summary" ||
            (intent.kind === "project" && intent.screen === "overview")
          )
        ) {
          throw new Error(
            `${detail.project.name} does not have a saved specification screen for that link.`
          );
        }

        setProjectData(null);
        setLoadingWorkspaceData(null);
        setSpecifyRequest(null);
        setActiveSpecifyJobId(null);
        setRouteNotice(null);
        setActiveSurface("summary");
        return;
      }

      const fixture = withScheduleEntriesFromDetail(
        parseProjectFixtureForDisplay(
          extractSpecifyResultFixture(detail.specResult.result)
        ),
        detail
      );
      if (intent?.kind === "summary") {
        replaceNextRouteSyncRef.current = true;
      }
      const nextScreen =
        intent?.kind === "project"
          ? intent.screen
          : intent?.kind === "documents"
            ? "documents"
            : intent?.kind === "spec" || intent?.kind === "specify"
              ? "spec"
              : "overview";
      const initialWorkspaceData = buildProjectWorkspaceDisplayData(fixture);
      const nextApartmentTypeId =
        intent?.kind === "spec"
          ? intent.apartmentTypeId
          : initialWorkspaceData.workspaceView.apartmentTypeId;

      if (
        intent?.kind === "spec" &&
        !initialWorkspaceData.workspaceViews.some(
          (view) => view.apartmentTypeId === intent.apartmentTypeId
        )
      ) {
        throw new Error(
          `${detail.project.name} does not include apartment type "${intent.apartmentTypeId}".`
        );
      }

      setLoadingWorkspaceData(initialWorkspaceData);
      setSelectedApartmentTypeId(nextApartmentTypeId);
      setSpecifyRequest(null);
      setActiveSpecifyJobId(null);
      setRouteNotice(null);
      setScreen(nextScreen);
      setSelectedDocumentId(
        intent?.kind === "documents" ? (intent.docId ?? null) : null
      );
      setDocumentRouteQuery(
        intent?.kind === "documents" ? intent.query : emptyDocumentRouteQuery()
      );
      setSpecTab(intent?.kind === "spec" ? intent.tab : "conn");
      setActiveSurface("project");

      await yieldToMainThread();
      const pricedFixture = await ensureProjectQuotePricing(fixture);
      const preparedWorkspaceData =
        pricedFixture === fixture
          ? initialWorkspaceData
          : buildProjectWorkspaceDisplayData(pricedFixture);

      if (preparedWorkspaceData !== initialWorkspaceData) {
        setLoadingWorkspaceData(preparedWorkspaceData);
        await yieldToMainThread();
      }

      const nextData = buildProjectDisplayData(pricedFixture, preparedWorkspaceData);

      setProjectData(nextData);
      setLoadingWorkspaceData(null);
      return;
    }

    throw new Error(
      `${detail.project.name} is saved with status "${detail.project.status}", which cannot be opened yet.`
    );
  };

  const navigateProjectScreen = (next: ScreenId) => {
    if (
      activeProjectSummary &&
      activeProjectDetail &&
      isOnboardingStatus(activeProjectDetail.project.status)
    ) {
      const intent =
        next === "spec"
          ? {
              kind: "spec" as const,
              projectId: activeProjectDetail.project.id,
              apartmentTypeId: preSpecificationApartmentTypeId(activeProjectDetail),
              tab: "conn" as const
            }
          : projectRouteIntentForScreen(activeProjectDetail.project.id, next);

      void routeProjectByStatus(
        activeProjectSummary,
        activeProjectDetail,
        intent
      ).catch((navigationError) => {
        setLoadError(errorMessage(navigationError));
      });
      return;
    }

    const needsProjectData = next !== "documents" && next !== "tender";

    if (!projectData && needsProjectData) {
      if (!activeProjectSummary || !activeProjectId) {
        setLoadError(
          "Project details are still loading. Stay on Tender and try that section again."
        );
        return;
      }

      const project = activeProjectSummary;
      const projectId = activeProjectId;
      const intent = projectRouteIntentForScreen(projectId, next);
      const navigationRequest = projectNavigationRequestRef.current + 1;
      const navigationController = new AbortController();

      projectNavigationRequestRef.current = navigationRequest;
      projectLoadAbortRef.current?.abort();
      projectLoadAbortRef.current = navigationController;
      setRoutingReady(false);
      setLoadError(null);

      void (
        activeProjectDetail
          ? Promise.resolve(activeProjectDetail)
          : getProject(projectId, { signal: navigationController.signal })
      )
        .then(async (detail) => {
          if (
            navigationController.signal.aborted ||
            projectNavigationRequestRef.current !== navigationRequest
          ) {
            return;
          }

          await routeProjectByStatus(project, detail, intent);

          if (
            !navigationController.signal.aborted &&
            projectNavigationRequestRef.current === navigationRequest
          ) {
            setRouteNotFound(null);
            setRoutingReady(true);
          }
        })
        .catch((navigationError) => {
          if (
            !navigationController.signal.aborted &&
            projectNavigationRequestRef.current === navigationRequest
          ) {
            setLoadError(errorMessage(navigationError));
            setRoutingReady(true);
          }
        })
        .finally(() => {
          if (projectLoadAbortRef.current === navigationController) {
            projectLoadAbortRef.current = null;
          }
        });
      return;
    }

    applyProjectScreenNavigation(next);
  };

  const handleCompareMatrixEdit = useCallback(
    async (override: CompareMatrixCellOverride) => {
      if (!activeProjectDetail) {
        throw new Error(
          "This project has not finished loading, so the matrix edit was not saved."
        );
      }

      const detail = await updateCompareMatrixCell(activeProjectDetail.project.id, {
        groupId: override.groupId,
        kind: override.kind,
        apartmentTypeId: override.apartmentTypeId,
        value: override.value,
        reason: override.reason,
        expectedRevision: activeProjectDetail.project.revision
      });

      if (!detail.specResult) {
        throw new Error("The saved project no longer has an active specification.");
      }

      const fixture = withScheduleEntriesFromDetail(
        parseProjectFixtureForDisplay(
          extractSpecifyResultFixture(detail.specResult.result)
        ),
        detail
      );
      const nextData = buildProjectDisplayData(
        await ensureProjectQuotePricing(fixture)
      );

      setActiveProjectDetail(detail);
      setActiveProjectId(detail.project.id);
      setActiveProjectLabel(detail.project.name);
      setProjectData(nextData);
      setSelectedApartmentTypeId(
        (current) => current ?? nextData.workspaceView.apartmentTypeId
      );
      setProjectsReloadKey((value) => value + 1);
      void getProjectReadiness(detail.project.id)
        .then((readiness) => {
          setActiveProjectDetail((current) =>
            current?.project.id === detail.project.id &&
            readiness.sourceVersion.projectRevision ===
              current.project.revision.toString()
              ? { ...current, readiness }
              : current
          );
        })
        .catch(() => undefined);
    },
    [activeProjectDetail]
  );

  const handleScheduleEntriesChanged = useCallback(
    (entries: readonly ScheduleEntry[]) => {
      setActiveProjectDetail((current) =>
        current ? { ...current, scheduleEntries: [...entries] } : current
      );
      setProjectData((current) =>
        current
          ? buildProjectDisplayData({
              ...current.projectFixture,
              scheduleEntries: [...entries],
              ...(activeProjectDetail?.notes
                ? { projectNotes: [...activeProjectDetail.notes] }
                : {})
            })
          : current
      );
    },
    [activeProjectDetail?.notes]
  );

  const handleReadinessWaiver = useCallback(
    async (blocker: ReadinessBlockerV1, reason: string) => {
      if (!activeProjectDetail) {
        throw new Error("The project readiness has not finished loading.");
      }
      if (!blocker.ledgerEntryRevision) {
        throw new Error(
          "This blocker is stale. Refresh the project before waiving it."
        );
      }
      const result = await waiveProjectReadinessEntry(activeProjectDetail.project.id, {
        entryId: blocker.ledgerEntryId,
        expectedRevision: blocker.ledgerEntryRevision,
        reason,
        issueProfile: "priced"
      });
      setActiveProjectDetail((current) =>
        current ? { ...current, readiness: result.readiness } : current
      );
    },
    [activeProjectDetail]
  );

  const handleReadinessAnswer = useCallback(
    async (blocker: ReadinessBlockerV1, answerReference: string) => {
      if (!activeProjectDetail) {
        throw new Error("The project readiness has not finished loading.");
      }
      if (!blocker.ledgerEntryRevision) {
        throw new Error(
          "This question is stale. Refresh the project before answering it."
        );
      }
      const result = await answerProjectReadinessEntry(activeProjectDetail.project.id, {
        entryId: blocker.ledgerEntryId,
        expectedRevision: blocker.ledgerEntryRevision,
        answerReference,
        issueProfile: "priced"
      });
      setActiveProjectDetail((current) =>
        current ? { ...current, readiness: result.readiness } : current
      );
    },
    [activeProjectDetail]
  );

  const handleReadinessChanged = useCallback((readiness: ProjectReadinessV1) => {
    setActiveProjectDetail((current) =>
      current ? { ...current, readiness } : current
    );
  }, []);

  useEffect(() => {
    if (!routeSyncEnabled) {
      return undefined;
    }

    const nextKey = routeKey(routeRequest);

    if (routeApplyKeyRef.current === nextKey) {
      return undefined;
    }

    if (routeApplyKeyRef.current !== null) {
      return undefined;
    }

    if (routeRequest.kind === "notFound") {
      setRouteNotice(null);
      setRouteNotFound({
        attemptedPath: routeRequest.attemptedPath,
        message: routeRequest.message
      });
      setRoutingReady(true);
      routeApplyKeyRef.current = nextKey;
      return undefined;
    }

    if (routeRequest.kind === "admin") {
      setRouteNotice(null);
      setRouteNotFound(null);
      setRoutingReady(true);
      setSpecifyRequest(null);
      setActiveSpecifyJobId(null);
      setSelectedItemId(null);
      setActiveSurface("admin");
      routeApplyKeyRef.current = nextKey;
      return undefined;
    }

    if (routeRequest.kind === "dashboard") {
      setRouteNotice(null);
      setRouteNotFound(null);
      setRoutingReady(true);
      setSpecifyRequest(null);
      setActiveSpecifyJobId(null);
      setSelectedItemId(null);
      setActiveSurface("dashboard");
      routeApplyKeyRef.current = nextKey;
      return undefined;
    }

    if (routeRequest.kind === "create" && routeRequest.projectId) {
      // RETIRE-H: project-scoped legacy create links redirect to the durable
      // upload-first route rather than rendering the old wizard.
      openTenderFlowForProject(routeRequest.projectId);
      setRouteNotice(
        "This old project setup link was retired; opening the upload-first stepper."
      );
      replaceNextRouteSyncRef.current = true;
      routeApplyKeyRef.current = nextKey;
      return undefined;
    }

    if (
      routeRequest.kind === "create" &&
      !routeRequest.projectId &&
      routeRequest.step !== "start"
    ) {
      // RETIRE-H: project-less legacy create links get a small retired note;
      // /create/start remains the transient new-flow entry while it obtains an id.
      setRouteNotice(null);
      setRouteNotFound({
        attemptedPath: routeToPath(routeRequest),
        message:
          "The old project setup flow was retired on 2026-08-20. Start from Projects to use the upload-first stepper.",
        retired: true
      });
      setRoutingReady(true);
      routeApplyKeyRef.current = nextKey;
      return undefined;
    }

    if (routeRequest.kind === "create" && !routeRequest.projectId) {
      const nextStep = clampCreateRouteStep(routeRequest.step, "start");

      if (nextStep !== routeRequest.step) {
        replaceNextRouteSyncRef.current = true;
        setRouteNotice(createStepGateNotice(routeRequest.step, nextStep));
      } else {
        setRouteNotice(null);
      }

      setRouteNotFound(null);
      setRoutingReady(true);
      setActiveProjectSummary(null);
      setActiveProjectLabel(null);
      setActiveProjectId(null);
      setActiveProjectDetail(null);
      setProjectData(null);
      setLoadingWorkspaceData(null);
      setSelectedItemId(null);
      setSelectedApartmentTypeId(null);
      setSpecTab("conn");
      setOnboardingStep(nextStep);
      setSpecifyRequest(null);
      setActiveSpecifyJobId(null);
      setActiveSurface("create");
      // /create/start is the transient new-flow entry until the project id is
      // persisted and the route canonicalises to /projects/:id/upload.
      setTenderFlowProjectId(null);
      routeApplyKeyRef.current = nextKey;
      return undefined;
    }

    // B-RELOAD: the upload-first stepper's own URL (/projects/:id/upload).
    // Reloading here -- or following this link back from anywhere -- must
    // resume the SAME project via NewTenderFlow, never mint a new one and
    // never fall into the legacy wizard. NewTenderFlow derives its own step
    // (Upload vs Viewer) from server truth once it hydrates, via its own
    // getProject(projectId) call -- same trust level openTenderFlowProjectScreen
    // already extends for the requirements/spec/export re-home. Deliberately
    // NOT gated on the dashboard summary list: a project reported back here
    // moments after NewTenderFlow created it (see onProjectIdChange) will not
    // exist in projectSummaryById yet -- nothing refetches that list on
    // creation -- and gating on it would show a false "not found" for a
    // project the server genuinely just created.
    if (routeRequest.kind === "tenderFlow") {
      const tenderFlowProject = projectSummaryById.get(routeRequest.projectId);

      setRouteNotFound(null);
      setRouteNotice(null);
      setRoutingReady(true);
      setActiveProjectSummary(tenderFlowProject ?? null);
      setActiveProjectLabel(tenderFlowProject?.name ?? null);
      setActiveProjectId(routeRequest.projectId);
      setActiveProjectDetail(null);
      setProjectData(null);
      setLoadingWorkspaceData(null);
      setSpecifyRequest(null);
      setActiveSpecifyJobId(null);
      setOnboardingStep("start");
      setActiveSurface("create");
      setTenderFlowProjectId(routeRequest.projectId);
      routeApplyKeyRef.current = nextKey;
      return undefined;
    }

    const projectId = projectIdForRoute(routeRequest);

    if (
      routeApplyKeyRef.current === null &&
      projectId &&
      activeProjectId === projectId &&
      (routeRequest.kind === "documents" ||
        (projectData &&
          (routeRequest.kind === "project" || routeRequest.kind === "spec")))
    ) {
      if (routeRequest.kind === "documents") {
        if (activeProjectDetail?.project.id !== projectId) {
          let cancelled = false;

          setRoutingReady(false);
          void getProject(projectId)
            .then((detail) =>
              routeProjectByStatus(
                projectSummaryById.get(projectId)!,
                detail,
                routeRequest
              )
            )
            .then(() => {
              if (!cancelled) {
                setRouteNotFound(null);
                setRoutingReady(true);
                routeApplyKeyRef.current = nextKey;
              }
            })
            .catch((error) => {
              if (!cancelled) {
                setRouteNotFound({
                  attemptedPath:
                    typeof globalThis.window === "undefined"
                      ? nextKey
                      : globalThis.window.location.pathname,
                  message: errorMessage(error)
                });
                setRoutingReady(true);
                routeApplyKeyRef.current = nextKey;
              }
            });

          return () => {
            cancelled = true;
          };
        }

        routeApplyKeyRef.current = nextKey;
        setSelectedDocumentId(routeRequest.docId ?? null);
        setDocumentRouteQuery(routeRequest.query);
        setScreen("documents");
        setSelectedItemId(null);
      } else if (routeRequest.kind === "spec") {
        if (!projectData) {
          return undefined;
        }

        const apartmentTypeExists = projectData.workspaceViews.some(
          (view) => view.apartmentTypeId === routeRequest.apartmentTypeId
        );

        if (!apartmentTypeExists) {
          setRouteNotFound({
            attemptedPath:
              typeof globalThis.window === "undefined"
                ? nextKey
                : globalThis.window.location.pathname,
            message: `${
              projectSummaryById.get(projectId)?.name ?? projectId
            } does not include apartment type "${routeRequest.apartmentTypeId}".`
          });
          setRoutingReady(true);
          routeApplyKeyRef.current = nextKey;
          return undefined;
        }

        routeApplyKeyRef.current = nextKey;
        setSelectedApartmentTypeId(routeRequest.apartmentTypeId);
        setSpecTab(routeRequest.tab);
        setScreen("spec");
      } else {
        routeApplyKeyRef.current = nextKey;
        setScreen(routeRequest.screen);
        setSelectedItemId(null);
      }

      setRouteNotFound(null);
      setRoutingReady(true);
      setSpecifyRequest(null);
      setActiveSpecifyJobId(null);
      setActiveSurface("project");
      return undefined;
    }

    if (!dashboardView) {
      return undefined;
    }

    const project = projectId ? projectSummaryById.get(projectId) : undefined;

    if (!project || !projectId) {
      setRouteNotFound({
        attemptedPath:
          typeof globalThis.window === "undefined"
            ? nextKey
            : globalThis.window.location.pathname,
        message: projectId
          ? `Project "${projectId}" was not found in saved projects.`
          : "That project link is missing a project id."
      });
      setRoutingReady(true);
      routeApplyKeyRef.current = nextKey;
      return undefined;
    }

    let cancelled = false;
    const routeLoadController = new AbortController();

    const routeCanRenderWithoutProjectData =
      routeRequest.kind === "project" && routeRequest.screen === "tender";

    projectLoadAbortRef.current?.abort();
    projectLoadAbortRef.current = routeLoadController;
    showProjectShell(project, routeRequest as ProjectRouteIntent);
    setRoutingReady(routeCanRenderWithoutProjectData);
    if (routeCanRenderWithoutProjectData) {
      routeApplyKeyRef.current = nextKey;
    }
    setDashboardActionError(null);

    void waitForWorkspaceShellPaint()
      .then(() => getProject(project.id, { signal: routeLoadController.signal }))
      .then(async (detail) => {
        if (cancelled || routeLoadController.signal.aborted) {
          return;
        }

        await routeProjectByStatus(project, detail, routeRequest as ProjectRouteIntent);

        if (!cancelled && !routeLoadController.signal.aborted) {
          setRouteNotFound(null);
          setRoutingReady(true);
          routeApplyKeyRef.current = nextKey;
        }
      })
      .catch((error) => {
        if (cancelled || routeLoadController.signal.aborted) {
          return;
        }

        setRouteNotFound({
          attemptedPath:
            typeof globalThis.window === "undefined"
              ? nextKey
              : globalThis.window.location.pathname,
          message: errorMessage(error)
        });
        setRoutingReady(true);
        routeApplyKeyRef.current = nextKey;
      })
      .finally(() => {
        if (projectLoadAbortRef.current === routeLoadController) {
          projectLoadAbortRef.current = null;
        }
        if (!cancelled) {
          setOpeningProjectId((current) => (current === project.id ? null : current));
        }
      });

    return () => {
      cancelled = true;
      routeLoadController.abort();
      if (projectLoadAbortRef.current === routeLoadController) {
        projectLoadAbortRef.current = null;
      }
    };
  }, [
    activeProjectId,
    dashboardView,
    projectData,
    projectSummaryById,
    routeRequest,
    routeSyncEnabled,
    showProjectShell
  ]);

  const withNotifications = (content: ReactNode) => (
    <NotificationProvider>
      {content}
      <ToastViewport />
    </NotificationProvider>
  );
  const openProjectById = (projectId: string) => {
    const project = projectSummaryById.get(projectId);

    if (!project) {
      setDashboardActionError(
        `Project "${projectId}" was not found in saved projects.`
      );
      return;
    }

    void openProject(project);
  };
  const withAppShell = (content: ReactNode) => (
    <AppShell
      activeSurface={activeSurface}
      activeScreen={activeSurface === "project" ? screen : null}
      activeProjectId={activeProjectId}
      activeProjectLabel={projectChromeLabel}
      projects={dashboardView?.cards ?? []}
      projectsLoading={projectsLoading}
      theme={theme}
      canEditLlmSettings={canEditLlmSettings}
      assistantOpen={appAssistant !== null && appAssistantOpen}
      assistant={appAssistant}
      guidance={(collapsed) =>
        routingReady && activeSurface !== "admin" ? (
          <Suspense fallback={null}>
            <ShowMeGuide
              collapsed={collapsed}
              activeSurface={activeSurface}
              activeScreen={activeSurface === "project" ? screen : null}
              readiness={activeProjectDetail?.readiness ?? null}
              hasProjects={Boolean(dashboardView?.cards.length)}
              onProjectNav={navigateProjectScreen}
            />
          </Suspense>
        ) : null
      }
      onTheme={setTheme}
      onDashboard={backToDashboard}
      onCreateProject={openCreateProject}
      onOpenLlmSettings={openLlmSettings}
      onOpenProject={openProjectById}
      onProjectNav={navigateProjectScreen}
    >
      {content}
    </AppShell>
  );

  if (routeNotFound) {
    return withNotifications(
      <div
        data-theme={theme}
        className="min-h-screen w-full bg-[var(--bg)] text-[var(--ink)]"
      >
        <SkipToContentLink />
        {withAppShell(
          <RouteNotFoundScreen
            attemptedPath={routeNotFound.attemptedPath}
            message={routeNotFound.message}
            retired={routeNotFound.retired}
            onDashboard={backToDashboard}
          />
        )}
      </div>
    );
  }

  if (activeSurface === "admin") {
    return withNotifications(
      <div
        data-theme={theme}
        className="min-h-screen w-full bg-[var(--bg)] text-[var(--ink)]"
      >
        <SkipToContentLink />
        {withAppShell(<AdminRouter section="llm" />)}
      </div>
    );
  }

  if (activeSurface === "dashboard") {
    const dashboardIssue = dashboardActionError ?? projectsError;

    return withNotifications(
      <div
        data-theme={theme}
        className="min-h-screen w-full bg-[var(--bg)] text-[var(--ink)]"
      >
        <SkipToContentLink />
        {withAppShell(
          !dashboardView ? (
            projectsError ? (
              <DashboardState
                title="Projects API unavailable"
                message={projectsError}
                actionLabel="Retry projects"
                onAction={retryProjectsLoad}
                recovery={{
                  title: "What to do now",
                  items: [
                    "Retry the saved-project request.",
                    "No benchmark data has replaced your saved projects.",
                    "When the API responds, the dashboard will load the current project list."
                  ]
                }}
                technicalDetails={dashboardSourceTechnicalDetails(
                  "dashboard-error-technical-details"
                )}
              />
            ) : (
              <DashboardLoadingState
                technicalDetails={dashboardSourceTechnicalDetails(
                  "dashboard-loading-technical-details"
                )}
              />
            )
          ) : (
            <>
              {dashboardIssue || openingProjectId ? (
                <DashboardNotice
                  tone={dashboardIssue ? "error" : "info"}
                  message={
                    dashboardIssue ??
                    `Opening ${projectSummaryById.get(openingProjectId ?? "")?.name ?? "saved project"}...`
                  }
                />
              ) : null}
              <Suspense
                fallback={
                  <DashboardLoadingState
                    technicalDetails={dashboardSourceTechnicalDetails(
                      "dashboard-loading-technical-details"
                    )}
                  />
                }
              >
                <ProjectsDashboard
                  view={dashboardView}
                  theme={theme}
                  onTheme={setTheme}
                  onSort={setDashboardSort}
                  onFilter={setDashboardFilter}
                  onSearch={setDashboardSearch}
                  onOpenProject={(project) => {
                    void openProject(project);
                  }}
                  onDeleteProject={deleteDashboardProject}
                  onCreateProject={openCreateProject}
                />
              </Suspense>
            </>
          )
        )}
      </div>
    );
  }

  if (activeSurface === "create") {
    // R1: "No create-wizard entry. A new project = name-less shell; step 1
    // IS the entry." A genuinely new entry has no project (openCreateProject
    // and the projectId-less /create route both null both fields); any
    // resumed wizard-status project always carries activeProjectDetail
    // before switching to this surface, so that path is untouched.
    const isNewTenderFlowEntry =
      onboardingStep === "start" && !activeProjectId && !activeProjectDetail;
    // B-RELOAD: reloading (or dashboard-reopening) a project that belongs to
    // the upload-first stepper resumes the SAME NewTenderFlow instance
    // instead of falling into the legacy wizard below -- tenderFlowProjectId
    // is only ever set by the tenderFlow route branch and the
    // dashboard-reopen branch in routeProjectByStatus, both gated on real
    // server-truth (tenderScope, including an explicit null) or an in-flight
    // fresh creation; never on routing/rendering alone (see FIX #2: no
    // create-as-a-side-effect).
    const isTenderFlowResume =
      onboardingStep === "start" && tenderFlowProjectId !== null;
    // A fresh flow reports its newly-created id so the URL can become durable,
    // but it is still the same mounted flow and must not be reinterpreted as a
    // direct resume (which would wait for a detail read before showing Upload).
    const isFreshTenderFlow = isNewTenderFlowEntry && tenderFlowProjectId !== null;

    return withNotifications(
      <div
        data-theme={theme}
        className="min-h-screen w-full bg-[var(--bg)] text-[var(--ink)]"
      >
        <SkipToContentLink />
        {withAppShell(
          <Suspense
            fallback={
              <SurfaceLoadingFallback
                eyebrow="Project"
                title="Loading project creator..."
                message="Preparing the project setup workspace and document review tools."
              />
            }
          >
            {routeNotice ? <DashboardNotice tone="info" message={routeNotice} /> : null}
            {isNewTenderFlowEntry || isTenderFlowResume ? (
              <NewTenderFlow
                key={
                  isFreshTenderFlow
                    ? "new-tender-flow"
                    : (tenderFlowProjectId ?? "new-tender-flow")
                }
                resumeProjectId={
                  isFreshTenderFlow ? undefined : (tenderFlowProjectId ?? undefined)
                }
                initialProjectDetail={activeProjectDetail ?? undefined}
                onProjectIdChange={setTenderFlowProjectId}
                onOpenProjectScreen={openTenderFlowProjectScreen}
              />
            ) : (
              <OnboardingFlow
                onStartSpecify={startSpecifyRun}
                onCancel={backToDashboard}
                initialStep={onboardingStep}
                onPersistentProjectId={handlePersistentOnboardingProjectId}
                onStepChange={setOnboardingStep}
                projectId={activeProjectId ?? undefined}
                projectDetail={activeProjectDetail ?? undefined}
                requirementsStage={requirementsSurface.requirementsStage}
                requirementsArtifact={requirementsSurface.requirementsArtifact}
                requirementsArtifactPath={requirementsSurface.requirementsArtifactPath}
              />
            )}
          </Suspense>
        )}
      </div>
    );
  }

  if (activeSurface === "specify" && specifyRequest) {
    return withNotifications(
      <div
        data-theme={theme}
        className="min-h-screen w-full bg-[var(--bg)] text-[var(--ink)]"
      >
        <SkipToContentLink />
        {withAppShell(
          <Suspense
            fallback={
              <SurfaceLoadingFallback
                eyebrow="Specify"
                title="Starting the specification..."
                message="Opening the run workspace, progress feed, and reviewed inputs."
              />
            }
          >
            <div className="border-b border-[var(--line)] bg-[var(--surface)] px-4 py-3 sm:px-6 xl:px-8">
              <div className="mx-auto max-w-[1320px]">
                <StepWayfinder
                  step="specify"
                  steps={specifyWayfinderSteps}
                  onStepClick={handleSpecifyWayfinderClick}
                  label="Project steps"
                />
              </div>
            </div>
            <SpecifyRunScreen
              request={specifyRequest}
              requirementsStage={requirementsSurface.requirementsStage}
              requirementsArtifact={requirementsSurface.requirementsArtifact}
              requirementsArtifactPath={requirementsSurface.requirementsArtifactPath}
              onComplete={handleSpecifiedProject}
              onCancel={cancelSpecifyRun}
              onBack={backToDashboard}
              projectId={activeProjectId ?? specifyRequest.input.projectId}
              jobId={activeSpecifyJobId ?? undefined}
            />
          </Suspense>
        )}
      </div>
    );
  }

  if (activeSurface === "summary" && activeProjectSummary) {
    return withNotifications(
      <div
        data-theme={theme}
        className="min-h-screen w-full bg-[var(--bg)] text-[var(--ink)]"
      >
        <SkipToContentLink />
        {withAppShell(
          <ProjectSummaryOnlyScreen
            project={activeProjectSummary}
            theme={theme}
            hasSavedInputs={
              activeProjectDetail ? projectHasSavedInputs(activeProjectDetail) : false
            }
            onTheme={setTheme}
            onBack={backToDashboard}
            onCreateProject={reviewActiveProjectInputs}
          />
        )}
      </div>
    );
  }

  return withNotifications(
    <div
      data-theme={theme}
      className="h-screen min-h-0 w-full overflow-hidden bg-[var(--bg)] text-[var(--ink)]"
    >
      <SkipToContentLink />
      {withAppShell(
        <div
          className="flex h-full min-h-0 min-w-0 overflow-hidden"
          data-workspace-shell-state={
            projectData
              ? "ready"
              : loadingWorkspaceData
                ? "workspace-ready"
                : preSpecificationWorkspace
                  ? "empty"
                  : "loading"
          }
        >
          {screen === "spec" && availableWorkspaceData && currentWorkspaceView ? (
            <ApartmentColumn
              activeApartmentTypeId={currentWorkspaceView.apartmentTypeId}
              views={availableWorkspaceData.workspaceViews}
              matrix={availableWorkspaceData.matrixView}
              onSelect={(apartmentTypeId) => {
                setSelectedApartmentTypeId(apartmentTypeId);
                setSelectedItemId(null);
              }}
            />
          ) : screen === "spec" && !preSpecificationWorkspace ? (
            <ApartmentColumnLoading
              count={activeProjectSummary?.apartmentTypeCount ?? 4}
            />
          ) : null}
          <main
            id="main-content"
            tabIndex={-1}
            aria-label={`${projectChromeLabel} workspace`}
            className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
          >
            {screen === "documents" ? (
              <>
                <a
                  className="skip-link"
                  href="#document-inventory-search"
                  onClick={(event) => {
                    event.preventDefault();
                    showDocumentWorkspacePane("inventory", "document-inventory-search");
                  }}
                >
                  Skip to document inventory
                </a>
                <a
                  className="skip-link"
                  href="#selected-document-heading"
                  onClick={(event) => {
                    event.preventDefault();
                    showDocumentWorkspacePane("detail", "selected-document-heading");
                  }}
                >
                  Skip to selected document
                </a>
              </>
            ) : null}
            {screen !== "overview" && screen !== "viewer" ? (
              <ProgressStepper
                active={screen}
                projectLabel={projectChromeLabel}
                readiness={activeProjectDetail?.readiness ?? null}
                onNav={navigateProjectScreen}
              />
            ) : null}
            {lazyRecoveryNotice ? (
              <DashboardNotice
                tone="error"
                message={`${lazyRecoveryNotice} failed to load before this reload. The URL was kept so you can retry the section in place.`}
              />
            ) : null}
            <ScreenRouter
              screen={screen}
              projectId={activeProjectId}
              documentId={selectedDocumentId}
              documentQuery={documentRouteQuery}
              mode={mode}
              projectData={projectData}
              tenderProject={
                activeProjectId
                  ? {
                      projectId: activeProjectId,
                      projectName: projectChromeLabel,
                      projectRecord: activeProjectDetail?.project
                    }
                  : null
              }
              workspaceData={loadingWorkspaceData}
              currentWorkspaceView={currentWorkspaceView}
              loadError={loadError}
              engineFixture={engineFixture}
              engineFixtureLoading={engineFixtureLoading}
              engineFixtureError={engineFixtureError}
              deliveredProjectNames={deliveredProjectNames}
              specTab={specTab}
              preSpecification={preSpecificationWorkspace}
              requirementsStale={requirementFingerprintState?.stale ?? false}
              requirementsStage={requirementsSurface.requirementsStage}
              requirementsArtifact={requirementsSurface.requirementsArtifact}
              requirementsArtifactPath={requirementsSurface.requirementsArtifactPath}
              costRollup={activeProjectDetail?.costRollup ?? null}
              readiness={activeProjectDetail?.readiness ?? null}
              policyFlagSet={activeProjectDetail?.project.policyFlagSet ?? null}
              sourceCoverageWarning={sourceCoverageWarningFromProjectDetail(
                activeProjectDetail
              )}
              onRerunSpecification={rerunCurrentSpecification}
              onRerunRequirements={rerunRequirementsStage}
              onStartSpecify={startSpecifyRun}
              {...(activeProjectDetail &&
              isOnboardingStatus(activeProjectDetail.project.status)
                ? { onContinueProjectSetup: continueActiveProjectSetup }
                : {})}
              lazyRouteRecoveryConsumedWithoutStorage={
                lazyRouteRecovery !== null && lazyRouteRecovery.source !== "session"
              }
              onMode={setMode}
              onNav={navigateProjectScreen}
              onDocumentRoute={handleDocumentRoute}
              onOpenItem={setSelectedItemId}
              onSelectApartmentType={(apartmentTypeId) => {
                setSelectedApartmentTypeId(apartmentTypeId);
                setSelectedItemId(null);
              }}
              onSpecTab={setSpecTab}
              onCompareMatrixEdit={handleCompareMatrixEdit}
              onScheduleEntriesChanged={handleScheduleEntriesChanged}
              onReadinessWaiver={handleReadinessWaiver}
              onReadinessAnswer={handleReadinessAnswer}
              onReadinessChanged={handleReadinessChanged}
            />
          </main>
          {selectedItem ? (
            <DetailDrawer
              item={selectedItem}
              apartmentTypeId={currentWorkspaceView?.apartmentTypeId ?? ""}
              engineFixture={engineFixture}
              engineFixtureLoading={engineFixtureLoading}
              engineFixtureError={engineFixtureError}
              onClose={() => setSelectedItemId(null)}
            />
          ) : null}
        </div>
      )}
    </div>
  );
}

function appAssistantSuggestions(activeSurface: ActiveSurface): readonly string[] {
  if (activeSurface === "dashboard") {
    return ["What needs attention?", "Which project should I open?", "Summarise risks"];
  }

  if (activeSurface === "specify") {
    return [
      "What is happening now?",
      "What should I check after this?",
      "Where are notes?"
    ];
  }

  return ["What is missing?", "What needs review?", "Summarise this screen"];
}

function assistantCopilotViewFor(
  activeSurface: ActiveSurface,
  screen: ScreenId
): CopilotTurnRequest["routeContext"]["view"] {
  if (activeSurface === "dashboard") {
    return "cockpit";
  }

  if (activeSurface === "specify") {
    return "impact";
  }

  switch (screen) {
    case "inputs":
    case "tender":
      return "apartments";
    case "requirements":
      return "requirements";
    case "spec":
    case "copilot":
    case "agentTrace":
    case "tokenUsage":
      return "impact";
    default:
      return "unknown";
  }
}

function SurfaceLoadingFallback({
  eyebrow,
  title,
  message
}: {
  eyebrow: string;
  title: string;
  message: string;
}) {
  return (
    <main
      id="main-content"
      tabIndex={-1}
      aria-label={title}
      className="min-h-screen px-8 py-8 xl:px-14"
    >
      <section className="tcsStepPanelEnter mx-auto max-w-[760px] rounded-[14px] border border-[var(--line)] bg-[var(--surface)] px-5 py-5 shadow-[0_18px_48px_-36px_rgba(35,33,30,.42)]">
        <div className="text-[11px] font-bold uppercase tracking-[.1em] text-[var(--faint)]">
          {eyebrow}
        </div>
        <h1 className="mt-2 text-[24px] font-extrabold tracking-[-.025em]">{title}</h1>
        <p className="mt-2 max-w-[560px] text-[13px] font-semibold leading-[1.55] text-[var(--muted)]">
          {message}
        </p>
        <div className="mt-5 h-1.5 overflow-hidden rounded-full bg-[var(--line2)]">
          <div className="h-full w-1/2 animate-tcsLoading rounded-full bg-[var(--ink)]" />
        </div>
      </section>
    </main>
  );
}

function DashboardState({
  title,
  message,
  actionLabel,
  onAction,
  recovery,
  technicalDetails
}: {
  title: string;
  message: string;
  actionLabel?: string | undefined;
  onAction?: (() => void) | undefined;
  recovery?:
    | {
        title: string;
        items: string[];
      }
    | undefined;
  technicalDetails?: ReactNode | undefined;
}) {
  return (
    <main
      id="main-content"
      tabIndex={-1}
      aria-label={title}
      className="min-h-screen px-8 py-8 xl:px-14"
    >
      <div className="mx-auto max-w-[860px] rounded-[14px] border border-[var(--line)] bg-[var(--surface)] px-5 py-5">
        <div className="text-[11px] font-bold uppercase tracking-[.1em] text-[var(--faint)]">
          Titan Specifier
        </div>
        <h1 className="mt-2 text-[24px] font-extrabold tracking-[-.025em]">{title}</h1>
        <p className="mt-2 text-[13px] font-semibold leading-[1.55] text-[var(--muted)]">
          {message}
        </p>
        {recovery ? (
          <section className="mt-4 rounded-[12px] border border-[#E9D3AD] bg-[#F6EEDD] px-4 py-3">
            <div className="text-[12px] font-extrabold text-[#8A6A2E]">
              {recovery.title}
            </div>
            <div className="mt-2 grid gap-1.5">
              {recovery.items.map((item) => (
                <div
                  key={item}
                  className="flex gap-2 text-[12.5px] font-semibold leading-[1.45] text-[var(--ink2)]"
                >
                  <span className="mt-[7px] h-1.5 w-1.5 flex-none rounded-full bg-[#8A6A2E]" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </section>
        ) : null}
        {actionLabel && onAction ? (
          <button
            type="button"
            onClick={onAction}
            className="mt-4 cursor-pointer rounded-[10px] bg-[var(--ink)] px-4 py-2.5 text-[12px] font-extrabold text-[var(--onSolid)]"
          >
            {actionLabel}
          </button>
        ) : null}
        {technicalDetails ? <div className="mt-5">{technicalDetails}</div> : null}
      </div>
    </main>
  );
}

function DashboardNotice({
  tone,
  message
}: {
  tone: "error" | "info";
  message: string;
}) {
  return (
    <div
      role={tone === "error" ? "alert" : "status"}
      className="border-b border-[var(--line)] bg-[var(--surface)] px-8 py-3 text-[12px] font-bold text-[var(--muted)] xl:px-14"
    >
      <div className="mx-auto max-w-[1320px]">
        <span className={tone === "error" ? "text-[#BC4A3C]" : "text-[var(--ink2)]"}>
          {message}
        </span>
      </div>
    </div>
  );
}

function RouteNotFoundScreen({
  attemptedPath,
  message,
  retired = false,
  onDashboard
}: {
  attemptedPath: string;
  message: string;
  retired?: boolean | undefined;
  onDashboard: () => void;
}) {
  return (
    <main
      id="main-content"
      tabIndex={-1}
      aria-label="Unavailable route"
      className="min-h-screen px-8 py-8 xl:px-14"
    >
      <div className="mx-auto max-w-[860px] rounded-[14px] border border-[var(--line)] bg-[var(--surface)] px-5 py-5">
        <div className="text-[11px] font-bold uppercase tracking-[.1em] text-[var(--faint)]">
          {retired ? "Retired link" : "Link not found"}
        </div>
        <h1 className="mt-2 text-[24px] font-extrabold tracking-[-.025em]">
          {retired
            ? "This old project setup link has been retired"
            : "This Titan Specifier link is not available"}
        </h1>
        <p className="mt-2 text-[13px] font-semibold leading-[1.55] text-[var(--muted)]">
          {message}
        </p>
        <div className="mt-4 rounded-[10px] border border-[var(--line)] bg-[var(--raised2)] px-3 py-2 text-[12px] font-bold text-[var(--muted)]">
          {attemptedPath}
        </div>
        <button
          type="button"
          onClick={onDashboard}
          className="mt-4 cursor-pointer rounded-[10px] bg-[var(--ink)] px-4 py-2.5 text-[12px] font-extrabold text-[var(--onSolid)]"
        >
          Back to Projects
        </button>
      </div>
    </main>
  );
}

const isOnboardingStatus = (
  status: ProjectDetailResponse["project"]["status"]
): boolean => status === "draft" || status === "inputs" || status === "requirements";

const createStepOrder: CreateRouteStep[] = [
  "start",
  "inputs",
  "requirements",
  "policy-flags"
];

const normaliseOnboardingProjectDetail = (
  detail: ProjectDetailResponse
): ProjectDetailResponse => ({
  ...detail,
  draft: normaliseCreateDraftForRoute(detail),
  documents: projectDocuments(detail)
});

const normaliseCreateDraftForRoute = (
  detail: ProjectDetailResponse
): CreateProjectDraft => {
  const base = emptyDraft();
  const draft = routeRecordValue(detail.draft);
  const meta = routeRecordValue(draft?.projectMeta);

  return {
    ...base,
    projectName: stringValue(draft?.projectName) ?? detail.project.name,
    projectMeta: {
      ...base.projectMeta,
      location: stringValue(meta?.location) ?? detail.project.location ?? "",
      client: stringValue(meta?.client) ?? detail.project.client ?? "",
      siteAddress: stringValue(meta?.siteAddress) ?? detail.project.siteAddress ?? "",
      consultant: stringValue(meta?.consultant) ?? detail.project.consultant ?? "",
      mainContractor:
        stringValue(meta?.mainContractor) ?? detail.project.mainContractor ?? ""
    },
    requirements: normaliseCreateRequirements(draft?.requirements),
    requirementProvenance:
      (routeRecordValue(draft?.requirementProvenance) as NonNullable<
        CreateProjectDraft["requirementProvenance"]
      >) ?? {},
    requirementTexts: arrayValue<CreateProjectDraft["requirementTexts"][number]>(
      draft?.requirementTexts
    ),
    apartmentTypes: arrayValue<CreateProjectDraft["apartmentTypes"][number]>(
      draft?.apartmentTypes
    ),
    sourceApartmentMetadata:
      (routeRecordValue(draft?.sourceApartmentMetadata) as NonNullable<
        CreateProjectDraft["sourceApartmentMetadata"]
      >) ?? {},
    unresolved: arrayValue<CreateProjectDraft["unresolved"][number]>(draft?.unresolved),
    policyFlagSet:
      normaliseSpecPolicyFlagSet(detail.project.policyFlagSet) ??
      normaliseSpecPolicyFlagSet(draft?.policyFlagSet) ??
      null,
    policyFlagDiscoveryUnavailable:
      booleanValue(draft?.policyFlagDiscoveryUnavailable) ?? false,
    policyFlagDiscoveryFailureReason: stringValue(
      draft?.policyFlagDiscoveryFailureReason
    ),
    ...(routeAcceptedEvidenceSet(draft?.acceptedEvidenceSet)
      ? { acceptedEvidenceSet: routeAcceptedEvidenceSet(draft?.acceptedEvidenceSet)! }
      : {})
  };
};

const routeAcceptedEvidenceSet = (
  value: unknown
): CreateProjectDraft["acceptedEvidenceSet"] | undefined => {
  const record = routeRecordValue(value);
  const documentIds = arrayValue<string>(record?.documentIds);
  const adapterArtifactIds = arrayValue<string>(record?.adapterArtifactIds);

  if (
    record?.schemaVersion !== "canonical-intake-bridge.v1" ||
    !stringValue(record.orgId) ||
    !stringValue(record.projectId) ||
    !stringValue(record.tenderId) ||
    !stringValue(record.packId) ||
    !stringValue(record.classificationArtifactId) ||
    !stringValue(record.acceptedAt)
  ) {
    return undefined;
  }

  return {
    schemaVersion: "canonical-intake-bridge.v1",
    orgId: stringValue(record.orgId)!,
    projectId: stringValue(record.projectId)!,
    tenderId: stringValue(record.tenderId)!,
    packId: stringValue(record.packId)!,
    classificationArtifactId: stringValue(record.classificationArtifactId)!,
    acceptedAt: stringValue(record.acceptedAt)!,
    documentIds,
    adapterArtifactIds
  };
};

const normaliseCreateRequirements = (
  value: unknown
): CreateProjectDraft["requirements"] => {
  const base = emptyDraft().requirements;
  const requirements = routeRecordValue(value);

  return {
    centralControlScreen:
      booleanValue(requirements?.centralControlScreen) ?? base.centralControlScreen,
    leakDetection: booleanValue(requirements?.leakDetection) ?? base.leakDetection,
    heatMetering: booleanValue(requirements?.heatMetering) ?? base.heatMetering,
    bmsIntegration: booleanValue(requirements?.bmsIntegration) ?? base.bmsIntegration
  };
};

function buildCurrentSpecifyInputFromDetail(
  detail: ProjectDetailResponse,
  notes: readonly ProjectNote[]
): SpecifyProjectInput {
  return adoptProjectIdForSpecifyInput(
    buildSpecifyProjectInputFromDraft(
      applyAppliedNotesToDraft(normaliseCreateDraftForRoute(detail), notes)
    ),
    detail.project.id
  );
}

function applyAppliedNotesToDraft(
  draft: CreateProjectDraft,
  notes: readonly ProjectNote[]
): CreateProjectDraft {
  const appliedNotes = notes.filter(noteIsAppliedToRequirements);

  if (appliedNotes.length === 0) {
    return draft;
  }

  const requirementTexts = [...draft.requirementTexts];

  for (const note of appliedNotes) {
    const promotedRequirementId = promotedRequirementIdForNote(note);
    const nextRequirement = promotedRequirementDraft(note, promotedRequirementId);
    const existingIndex = requirementTexts.findIndex(
      (requirement) =>
        requirement.sourceNoteId === note.id || requirement.id === promotedRequirementId
    );

    if (existingIndex >= 0) {
      const existing = requirementTexts[existingIndex]!;

      requirementTexts[existingIndex] = {
        ...existing,
        ...nextRequirement,
        id: existing.id || promotedRequirementId
      };
      continue;
    }

    requirementTexts.push(nextRequirement);
  }

  return { ...draft, requirementTexts };
}

function requirementsFromSpecifyInput(input: SpecifyProjectInput): Requirement[] {
  const requirementsById = new Map<string, Requirement>();

  for (const apartmentType of input.apartmentTypes) {
    for (const requirement of apartmentType.requirements) {
      requirementsById.set(requirement.id, requirement);
    }
  }

  return [...requirementsById.values()];
}

function adoptProjectIdForSpecifyInput(
  input: SpecifyProjectInput,
  projectId: string | null
): SpecifyProjectInput {
  if (!projectId || input.projectId === projectId) {
    return input;
  }

  const previousProjectId = input.projectId;

  return {
    ...input,
    projectId,
    ...(input.policyFlagSet
      ? {
          policyFlagSet: rewritePolicyFlagSetProjectId(input.policyFlagSet, projectId)
        }
      : {}),
    apartmentTypes: input.apartmentTypes.map((apartmentType) => {
      const apartmentTypeId = rewriteProjectScopedId(
        apartmentType.id,
        previousProjectId,
        projectId
      );

      return {
        ...apartmentType,
        id: apartmentTypeId,
        takeoffRows: apartmentType.takeoffRows.map((row) => ({
          ...row,
          apartmentTypeId: rewriteProjectScopedId(
            row.apartmentTypeId,
            previousProjectId,
            projectId
          ),
          rawFields: { ...row.rawFields, Project: projectId },
          sourceRefs: row.sourceRefs.map((sourceRef) =>
            rewriteProjectSourceRef(sourceRef, previousProjectId, projectId)
          )
        })),
        requirements: apartmentType.requirements.map((requirement) => ({
          ...requirement,
          id: rewriteProjectScopedId(requirement.id, previousProjectId, projectId),
          projectId,
          scope: requirement.scope.refId
            ? {
                ...requirement.scope,
                refId: rewriteProjectScopedId(
                  requirement.scope.refId,
                  previousProjectId,
                  projectId
                )
              }
            : requirement.scope,
          sourceRefs: requirement.sourceRefs.map((sourceRef) =>
            rewriteProjectSourceRef(sourceRef, previousProjectId, projectId)
          )
        }))
      };
    })
  };
}

function rewriteProjectScopedId(
  value: string,
  previousProjectId: string,
  projectId: string
): string {
  if (value === previousProjectId) {
    return projectId;
  }

  return value.startsWith(`${previousProjectId}-`)
    ? `${projectId}${value.slice(previousProjectId.length)}`
    : value;
}

function rewriteProjectSourceRef<T extends SourceRef>(
  sourceRef: T,
  previousProjectId: string,
  projectId: string
): T {
  return {
    ...sourceRef,
    sourceId: rewriteProjectScopedId(sourceRef.sourceId, previousProjectId, projectId),
    locator: sourceRef.locator.replaceAll(previousProjectId, projectId)
  };
}

type SnapshotRequirementFingerprint =
  | { kind: "full"; value: string }
  | { kind: "rows"; value: string };

function snapshotRequirementsFingerprint(
  fixture: ProjectFixtureWithLaneB
): SnapshotRequirementFingerprint {
  if (typeof fixture.project.requirementsFingerprint === "string") {
    const fingerprint = fixture.project.requirementsFingerprint.trim();

    if (fingerprint) {
      return { kind: "full", value: fingerprint };
    }
  }

  return {
    kind: "rows",
    value: fixtureRequirementRowsFingerprint(fixture.project.requirements)
  };
}

function fixtureRequirementRowsFromRequirements(
  requirements: readonly Requirement[]
): ProjectFixtureWithLaneB["project"]["requirements"] {
  return requirements.map((requirement) => {
    const row: ProjectFixtureWithLaneB["project"]["requirements"][number] = {
      id: requirement.id,
      type: requirement.type,
      status: requirement.status,
      interpretedText: requirement.interpretedText,
      priority: requirement.priority,
      confidence: requirement.confidence,
      sourceRefs: requirement.sourceRefs
    };

    if (requirement.tags) {
      row.tags = requirement.tags;
    }

    if (requirement.productQuantity) {
      row.productQuantity = requirement.productQuantity;
    }

    return row;
  });
}

function fixtureRequirementRowsFingerprint(
  rows: readonly ProjectFixtureWithLaneB["project"]["requirements"][number][]
): string {
  const canonical = rows
    .map((row) => ({
      id: row.id,
      type: row.type,
      status: row.status,
      interpretedText: row.interpretedText,
      priority: row.priority,
      tags: row.tags,
      productQuantity: row.productQuantity,
      confidence: row.confidence,
      sourceRefs: row.sourceRefs
    }))
    .sort((left, right) => left.id.localeCompare(right.id));

  return `requirements-row-fingerprint-v1:${stableRequirementsStringHash(
    stableRequirementsStringify(canonical)
  )}`;
}

function stableRequirementsStringify(value: unknown): string {
  if (value === null || typeof value !== "object") {
    return JSON.stringify(value);
  }

  if (Array.isArray(value)) {
    return `[${value.map(stableRequirementsStringify).join(",")}]`;
  }

  const record = value as Record<string, unknown>;
  const entries = Object.keys(record)
    .sort()
    .flatMap((key) =>
      record[key] === undefined
        ? []
        : [`${JSON.stringify(key)}:${stableRequirementsStringify(record[key])}`]
    );

  return `{${entries.join(",")}}`;
}

function stableRequirementsStringHash(value: string): string {
  let hash = 0xcbf29ce484222325n;
  const prime = 0x100000001b3n;
  const mask = 0xffffffffffffffffn;

  for (let index = 0; index < value.length; index += 1) {
    hash ^= BigInt(value.charCodeAt(index));
    hash = (hash * prime) & mask;
  }

  return hash.toString(16).padStart(16, "0");
}

function normaliseSpecPolicyFlagSet(value: unknown): SpecPolicyFlagSet | undefined {
  const parsed = SpecPolicyFlagSetSchema.safeParse(value);

  return parsed.success ? parsed.data : undefined;
}

const projectDocuments = (
  detail: ProjectDetailResponse
): ProjectDetailResponse["documents"] =>
  Array.isArray(detail.documents) ? detail.documents : [];

const projectHasWorkspaceContent = (detail: ProjectDetailResponse): boolean =>
  projectDocuments(detail).length > 0 || hasDraftInputEvidence(detail.draft);

// The server populates ProjectDetailResponse.tenderScope from the same
// tender-intake repository lookup the Tender ingestion screen itself uses
// (resolveProjectTenderScope in apps/server/src/projectsApi.ts) -- that is
// the primary, cross-browser signal that a project has tender-intake
// content. Older responses/fixtures may omit the field entirely, and the
// lookup itself can degrade to `null` (unsupported store, transient error),
// so this also falls back to a per-browser localStorage signal: the Tender
// ingestion screen persists its last-known pack scope to localStorage under
// this key on recovery/upload (see tenderScopeStorageKey in
// features/tender/TenderIngestionScreen.tsx). Routing reads that same key
// directly -- rather than importing the lazily-loaded tender module, which
// would pull it into this entry chunk -- as a cheap, synchronous fallback.
const TENDER_SCOPE_STORAGE_PREFIX = "cse:tender-ingestion:active-scope:";

const projectHasLocalTenderScopeFallback = (projectId: string): boolean => {
  if (typeof globalThis.window === "undefined") {
    return false;
  }

  try {
    return (
      globalThis.window.localStorage.getItem(
        `${TENDER_SCOPE_STORAGE_PREFIX}${projectId}`
      ) !== null
    );
  } catch {
    return false;
  }
};

const projectHasKnownTenderScope = (detail: ProjectDetailResponse): boolean =>
  detail.tenderScope != null || projectHasLocalTenderScopeFallback(detail.project.id);

// `undefined` is how older fixtures/callers represent a response that did not
// include tender-scope resolution. The server's explicit `null` means it did
// perform the lookup and found no active pack, which is still enough to
// identify the upload-first tender flow on a dashboard reopen.
const projectHasResolvedTenderScope = (detail: ProjectDetailResponse): boolean =>
  detail.tenderScope !== undefined ||
  projectHasLocalTenderScopeFallback(detail.project.id);

const workspaceIntentForProjectContent = (
  detail: ProjectDetailResponse
): Extract<ProjectRouteIntent, { kind: "documents" | "spec" }> =>
  projectDocuments(detail).length > 0
    ? {
        kind: "documents",
        projectId: detail.project.id,
        query: emptyDocumentRouteQuery()
      }
    : {
        kind: "spec",
        projectId: detail.project.id,
        apartmentTypeId: preSpecificationApartmentTypeId(detail),
        tab: "conn"
      };

const arrayValue = <T,>(value: unknown): T[] =>
  Array.isArray(value) ? (value as T[]) : [];

const routeRecordValue = (value: unknown): Record<string, unknown> | undefined =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;

const stringValue = (value: unknown): string | undefined =>
  typeof value === "string" ? value : undefined;

const booleanValue = (value: unknown): boolean | undefined =>
  typeof value === "boolean" ? value : undefined;

const createRouteStepForProject = (detail: ProjectDetailResponse): CreateRouteStep => {
  const preferredStep =
    detail.project.onboardingStep === "inputs" ||
    detail.project.onboardingStep === "requirements" ||
    detail.project.onboardingStep === "policy-flags"
      ? detail.project.onboardingStep
      : detail.project.status === "inputs" || detail.project.status === "requirements"
        ? detail.project.status
        : "start";

  return clampCreateRouteStep(preferredStep, furthestLegitimateCreateStep(detail));
};

const furthestLegitimateCreateStep = (
  detail: ProjectDetailResponse
): CreateRouteStep => {
  if (hasMinimumCreateApartmentData(detail.draft)) {
    return "policy-flags";
  }

  if (hasSavedCreateInputs(detail)) {
    return "inputs";
  }

  return "start";
};

const clampCreateRouteStep = (
  requested: CreateRouteStep,
  allowed: CreateRouteStep
): CreateRouteStep =>
  createStepOrder[
    Math.min(createStepOrder.indexOf(requested), createStepOrder.indexOf(allowed))
  ] ?? "start";

const createStepGateNotice = (
  requested: CreateRouteStep,
  allowed: CreateRouteStep
): string =>
  `That wizard link opens after ${createStepGatePrerequisite(
    requested
  )}. We returned you to ${createStepLabel(allowed)} so the project stays consistent.`;

const createStepGatePrerequisite = (step: CreateRouteStep): string =>
  step === "policy-flags" || step === "requirements"
    ? "saved apartment inputs exist"
    : "a project name is saved";

const createStepLabel = (step: CreateRouteStep): string =>
  step === "policy-flags"
    ? "Controls policy flags"
    : step === "requirements"
      ? "Requirements"
      : step === "inputs"
        ? "Inputs"
        : "Start";

const hasSavedCreateInputs = (detail: ProjectDetailResponse): boolean =>
  hasDraftInputEvidence(detail.draft) ||
  projectDocuments(detail).some(
    (document) =>
      document.status === "parsed" ||
      document.text.trim().length > 0 ||
      document.extraction !== null
  );

const hasDraftInputEvidence = (draft: CreateProjectDraft | null): boolean => {
  const record = routeRecordValue(draft);

  return (
    record !== undefined &&
    (arrayValue(record.apartmentTypes).length > 0 ||
      arrayValue(record.requirementTexts).length > 0 ||
      arrayValue(record.unresolved).length > 0 ||
      Object.values(normaliseCreateRequirements(record.requirements)).some(Boolean))
  );
};

const hasMinimumCreateApartmentData = (draft: CreateProjectDraft | null): boolean =>
  arrayValue<CreateProjectDraft["apartmentTypes"][number]>(
    routeRecordValue(draft)?.apartmentTypes
  ).some((apartmentType) => {
    try {
      return hasMinimumApartmentTypeData(apartmentType);
    } catch {
      return false;
    }
  });

const projectHasSavedInputs = (detail: ProjectDetailResponse): boolean =>
  detail.draft !== null || projectDocuments(detail).length > 0;

const BROWSER_MOCK_SPEC_STORAGE_PREFIX = "automatry.specify.browserMockResult.";

const canUseBrowserMockSpecResult = (
  intent: ProjectRouteIntent | undefined
): intent is Extract<ProjectRouteIntent, { kind: "project" | "spec" }> =>
  intent?.kind === "project" || intent?.kind === "spec";

const persistBrowserMockSpecResult = (projectId: string, result: unknown): void => {
  const storage = browserStorage();

  if (!storage || !isBrowserMockSpecResult(projectId, result)) {
    return;
  }

  storage.setItem(
    browserMockSpecStorageKey(projectId),
    JSON.stringify({
      projectId,
      result
    })
  );
};

const loadBrowserMockSpecResult = async (
  projectId: string
): Promise<{ result: unknown; data: ProjectDisplayData } | null> => {
  const storage = browserStorage();

  if (!storage) {
    return null;
  }

  const key = browserMockSpecStorageKey(projectId);
  const raw = storage.getItem(key);

  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as { projectId?: unknown; result?: unknown };
    const result = parsed.result;

    if (parsed.projectId !== projectId || !isBrowserMockSpecResult(projectId, result)) {
      storage.removeItem(key);
      return null;
    }

    const fixture = parseProjectFixtureForDisplay(extractSpecifyResultFixture(result));

    if (fixture.project.projectId !== projectId) {
      storage.removeItem(key);
      return null;
    }

    return {
      result,
      data: buildProjectDisplayData(await ensureProjectQuotePricing(fixture))
    };
  } catch {
    storage.removeItem(key);
    return null;
  }
};

const isBrowserMockSpecResult = (projectId: string, result: unknown): boolean => {
  const resultRecord = recordValue(result);
  const provider = recordValue(resultRecord?.provider);

  return resultRecord?.projectId === projectId && provider?.mode === "mock";
};

const browserMockSpecStorageKey = (projectId: string): string =>
  `${BROWSER_MOCK_SPEC_STORAGE_PREFIX}${projectId}`;

const browserStorage = (): Storage | null => {
  try {
    return globalThis.localStorage;
  } catch {
    return null;
  }
};

const specifyRunRequestFromProject = (
  detail: ProjectDetailResponse
): SpecifyRunRequest | null => {
  const request = detail.activeJob?.request;
  const input =
    specifyInputFromUnknown(request) ??
    specifyInputFromUnknown(recordValue(request)?.input);

  return input ? { input, projectName: input.projectName } : null;
};

const specifyInputFromUnknown = (value: unknown): SpecifyProjectInput | null => {
  const record = recordValue(value);

  if (!record) {
    return null;
  }

  return typeof record.projectId === "string" &&
    typeof record.projectName === "string" &&
    Array.isArray(record.apartmentTypes)
    ? (value as SpecifyProjectInput)
    : null;
};

const sourceCoverageWarningFromProjectDetail = (
  detail: ProjectDetailResponse | null
): SourceCoverageWarning | null => {
  const record = recordValue(detail?.draft);
  const warning = recordValue(record?.sourceCoverageWarning);

  if (!warning || warning.status !== "partial") {
    return detail ? sourceCoverageWarningFromDocuments(detail.documents) : null;
  }

  const documentTitles = stringArrayValue(warning.documentTitles);
  const readPageRanges = stringArrayValue(warning.readPageRanges);
  const unreadPages = numberArrayValue(warning.unreadPages);
  const documentCount =
    typeof warning.documentCount === "number" && warning.documentCount > 0
      ? Math.floor(warning.documentCount)
      : documentTitles.length;
  const message = typeof warning.message === "string" ? warning.message : "";
  const nextStep = typeof warning.nextStep === "string" ? warning.nextStep : "";

  if (documentCount === 0 && readPageRanges.length === 0 && unreadPages.length === 0) {
    return null;
  }

  return {
    status: "partial",
    documentCount: Math.max(1, documentCount),
    documentTitles,
    readPageRanges,
    unreadPages,
    message:
      message ||
      `Partial parse: ${Math.max(1, documentCount)} source${
        Math.max(1, documentCount) === 1 ? "" : "s"
      } had unread pages.`,
    nextStep:
      nextStep ||
      "Add the missing pages as pasted text or a clearer source before relying on the extracted values."
  };
};

const recordValue = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === "object" ? (value as Record<string, unknown>) : null;

const stringArrayValue = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter(
        (item): item is string => typeof item === "string" && item.trim().length > 0
      )
    : [];

const numberArrayValue = (value: unknown): number[] =>
  Array.isArray(value)
    ? value.filter((item): item is number => Number.isInteger(item) && item > 0)
    : [];

const errorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : "Unknown projects API error.";

const safeId = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 72) || "project";

const stablePreviewHash = (value: unknown): string => {
  const text = JSON.stringify(value);
  let hash = 0x811c9dc5;

  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }

  return (hash >>> 0).toString(16).padStart(8, "0").padEnd(64, "0");
};

const buildProjectTakeoffRowForPreview = (
  projectData: ProjectDisplayData,
  solution: ProjectDisplayData["apartmentSolutions"][number]
): ApartmentTakeoffRow => {
  const fixture = projectData.projectFixture;
  const displayRow = fixture.takeoffRows.find(
    (row) => row.apartmentTypeId === solution.apartmentTypeId
  );
  const sourceRefs = sourceRefsForPreview(projectData, solution);
  const countZones = (zoneKind: "fcu" | "wufh" | "eufh" | "heat_only") =>
    solution.logicalZones.filter((zone) => zone.zoneKind === zoneKind).length;
  const countDevicesByRole = (predicate: (role: string) => boolean) =>
    solution.devices.reduce(
      (total, device) => total + (predicate(device.role) ? device.quantity : 0),
      0
    );
  const pointDemands =
    (
      solution as ProjectDisplayData["apartmentSolutions"][number] & {
        pointDemands?: {
          demandKind?: string;
          points?: Partial<PointVector>;
        }[];
      }
    ).pointDemands ?? [];
  const towelRailCount = pointDemands
    .filter((pointDemand) => pointDemand.demandKind === "towel_rail_load_relay_output")
    .reduce(
      (total, pointDemand) =>
        total + (pointDemand.points?.do ?? 0) + (pointDemand.points?.relay ?? 0),
      0
    );
  const localUiPointCount = countDevicesByRole((role) => role.startsWith("local_ui_"));
  const centralScreenCount = countDevicesByRole((role) =>
    role.includes("central_control_screen")
  );

  return {
    apartmentTypeId: solution.apartmentTypeId,
    rawLabel: displayRow?.rawLabel ?? labelFromId(solution.apartmentTypeId),
    quantity: displayRow?.quantity ?? solution.quantity,
    ...(displayRow?.beds !== undefined ? { beds: displayRow.beds } : {}),
    fcuCount: displayRow?.fcuCount ?? countZones("fcu"),
    towelRailCount,
    wufhZoneCount: countZones("wufh"),
    eufhZoneCount: countZones("eufh"),
    standaloneHeatOnlyZoneCount: countZones("heat_only"),
    thermostatUiPointCount: localUiPointCount,
    centralControlScreenCount: centralScreenCount,
    sourceRefs,
    rawFields: {
      Project: fixture.project.projectId,
      "Apartment type": displayRow?.rawLabel ?? solution.apartmentTypeId,
      Homes: displayRow?.quantity ?? solution.quantity,
      Bedrooms: displayRow?.beds ?? 0,
      "Fan-coil zones": displayRow?.fcuCount ?? countZones("fcu"),
      "Wet underfloor heating zones": countZones("wufh"),
      "Electric underfloor heating zones": countZones("eufh"),
      "Heat-only zones": countZones("heat_only"),
      "Towel rail zones": towelRailCount,
      "Thermostat/control points": localUiPointCount,
      "Central screens": centralScreenCount
    },
    normalizedFrom: [
      {
        rawHeading: "Generated apartment solution",
        canonicalField: "apartmentTypeId",
        confidence: 0.86
      }
    ],
    confidence: 0.86,
    warnings: []
  };
};

const sourceRefsForPreview = (
  projectData: ProjectDisplayData,
  solution: ProjectDisplayData["apartmentSolutions"][number]
): SourceRef[] => {
  if (solution.sourceRefs.length > 0) {
    return solution.sourceRefs;
  }

  const requirementSourceRefs = projectData.projectFixture.project.requirements.flatMap(
    (requirement) => requirement.sourceRefs ?? []
  );

  if (requirementSourceRefs.length > 0) {
    return requirementSourceRefs;
  }

  return [
    {
      sourceId: `${projectData.projectFixture.project.projectId}-generated-preview`,
      sourceKind: "manual_entry",
      authorityClass: "takeoff",
      locator: `${projectData.projectFixture.fixture.sourceInput} / generated project preview`
    }
  ];
};

const buildProjectRequirementsForPreview = (
  projectData: ProjectDisplayData
): Requirement[] =>
  projectData.projectFixture.project.requirements.map((requirement) => ({
    id: requirement.id,
    projectId: projectData.projectFixture.project.projectId,
    scope: {
      level: "project",
      refId: projectData.projectFixture.project.projectId,
      label: projectData.cockpitView.title
    },
    type: requirement.type,
    interpretedText: requirement.interpretedText,
    priority: "default",
    sourceRefs: requirement.sourceRefs?.length
      ? requirement.sourceRefs
      : sourceRefsForPreview(projectData, projectData.apartmentSolutions[0]!),
    status: requirement.status,
    confidence: requirement.confidence,
    overrideState: "none",
    interactsWith: [],
    canSubstituteFor: [],
    createdBy: "agent",
    createdAt: projectData.projectFixture.fixture.generatedAt
  }));
